import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  Mail,
  MessageSquare,
  ChevronRight,
  Search,
  UserCheck,
  UserX,
  Send,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  BarChart3,
  Eye,
  BookOpen,
  SkipForward,
  HelpCircle,
  Flag,
  ShieldAlert,
  Ban,
  ShieldCheck,
  FileWarning,
  Ticket,
  Settings2,
  Crown,
  Plus,
  Trash2,
  Edit3,
  Copy,
  Calendar,
  Percent,
  IndianRupee,
  DollarSign,
  Zap,
  Pause,
  Play,
  Inbox,
  Paperclip,
  Reply,
  Bell,
  Info,
  AlertTriangle,
  Lightbulb,
  Megaphone,
  Bot,
  Smartphone,
  ToggleLeft,
  ToggleRight,
  ArrowUpDown,
  MessageCircle,
  Receipt,
  Gift,
  RotateCcw,
  FileText
} from 'lucide-react';
import { adminAPI, adminCouponAPI, adminPlanAPI, adminSubscriptionsAPI, adminNotificationAPI, adminNotificationChannelsAPI, adminReferralAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import Alert from '../components/common/Alert';
import ConfirmModal from '../components/common/ConfirmModal';
import DateTimeInput from '../components/common/DateTimeInput';
import SettlementsTab from '../components/admin/SettlementsTab';
import BlogTab from '../components/admin/BlogTab';
import AiConfigTab from '../components/admin/AiConfigTab';
import { getCountryByCode } from '../config/countries';

// Helper function to format date with leading zeros (DD-MM-YYYY)
const formatAdminDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'dashboard');

  // Update active tab when URL changes
  useEffect(() => {
    if (tabFromUrl && ['dashboard', 'users', 'leads', 'feedback', 'reports', 'coupons', 'plans', 'subscriptions', 'settlements', 'blog', 'emails', 'notifications', 'notification-channels', 'referrals', 'ai-config'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'leads', label: 'Leads', icon: Mail },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare },
    // { id: 'emails', label: 'Emails', icon: Inbox }, // DISABLED — using Hostinger email inbox directly. To re-enable: uncomment this + the render line below
    // { id: 'reports', label: 'Reports', icon: Flag }, // DISABLED — no user-facing submit UI, debt/receivable reports not in use
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'plans', label: 'Plans', icon: Settings2 },
    { id: 'subscriptions', label: 'Subscriptions', icon: Crown },
    { id: 'settlements', label: 'Settlements', icon: Receipt },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'notification-channels', label: 'Notification Channels', icon: Smartphone },
    { id: 'referrals', label: 'Referrals', icon: Gift },
    { id: 'ai-config', label: 'AI Config', icon: Bot },
    { id: 'blog', label: 'Blog', icon: FileText }
  ];

  // Find active tab label for mobile header
  const activeTabInfo = tabs.find(t => t.id === activeTab);

  return (
    <div className="space-y-4 xl:space-y-6">
      {/* Mobile Header - just shows current tab name */}
      <div className="xl:hidden">
        <h1 className="text-lg font-bold text-surface-900">{activeTabInfo?.label || 'Admin'}</h1>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:block page-header">
        <h1 className="page-title">Admin Panel</h1>
        <p className="page-subtitle">Manage users, leads, and feedback</p>
      </div>

      <div>
        {/* Content - full width, sidebar handles tab navigation */}
        <div>
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'leads' && <LeadsTab />}
          {activeTab === 'feedback' && <FeedbackTab />}
          {/* {activeTab === 'emails' && <ReceivedEmailsTab />} */}{/* DISABLED — using Hostinger email inbox */}
          {activeTab === 'reports' && <ReportsTab />}
          {activeTab === 'coupons' && <CouponsTab />}
          {activeTab === 'plans' && <PlansTab />}
          {activeTab === 'subscriptions' && <SubscriptionsTab />}
          {activeTab === 'settlements' && <SettlementsTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'notification-channels' && <NotificationChannelsTab />}
          {activeTab === 'referrals' && <ReferralsTab />}
          {activeTab === 'ai-config' && <AiConfigTab />}
          {activeTab === 'blog' && <BlogTab />}
        </div>
      </div>
    </div>
  );
}

// Dashboard Tab
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { data } = await adminAPI.getStats();
      setStats(data.data);
    } catch (err) {
      setError(err.error || 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <Alert variant="error" message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Users Stats */}
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-surface-900">Users</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-surface-600">Total</span>
              <span className="font-semibold">{stats?.users?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-income-600">Active</span>
              <span className="font-semibold text-income-600">{stats?.users?.active || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-expense-600">Inactive</span>
              <span className="font-semibold text-expense-600">{stats?.users?.inactive || 0}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-blue-600 text-sm font-medium">Active (last 10 days)</span>
              <span className="font-semibold text-blue-600">{stats?.users?.activeLast10Days || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500 text-sm">This Month</span>
              <span className="font-medium text-surface-700">{stats?.users?.thisMonth || 0}</span>
            </div>
          </div>
        </div>

        {/* Leads Stats */}
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Mail className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-surface-900">Leads</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-surface-600">Total</span>
              <span className="font-semibold">{stats?.leads?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-600">New</span>
              <span className="font-semibold text-amber-600">{stats?.leads?.new || 0}</span>
            </div>
          </div>
        </div>

        {/* Feedback Stats */}
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <MessageSquare className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-surface-900">Feedback</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-surface-600">Total</span>
              <span className="font-semibold">{stats?.feedback?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-purple-600">New</span>
              <span className="font-semibold text-purple-600">{stats?.feedback?.new || 0}</span>
            </div>
          </div>
        </div>

        {/* Subscriptions Stats */}
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Crown className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-surface-900">Subscriptions</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-surface-600">Active</span>
              <span className="font-semibold text-green-600">{stats?.subscriptions?.active || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-600">Trial</span>
              <span className="font-semibold text-amber-600">{stats?.subscriptions?.trial || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500">Expired</span>
              <span className="font-semibold text-surface-500">{stats?.subscriptions?.expired || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-500">Cancelled</span>
              <span className="font-semibold text-surface-500">{stats?.subscriptions?.cancelled || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Paused</span>
              <span className="font-semibold text-blue-600">{stats?.subscriptions?.paused || 0}</span>
            </div>
            {(stats?.subscriptions?.cancelledThisMonth > 0) && (
              <div className="flex justify-between">
                <span className="text-expense-600">Cancelled (this month)</span>
                <span className="font-semibold text-expense-600">{stats.subscriptions.cancelledThisMonth}</span>
              </div>
            )}
            {(stats?.subscriptions?.revenue > 0) && (
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-surface-500 text-sm">Total Revenue</span>
                <span className="font-medium text-surface-700" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{((stats.subscriptions.revenue) / 100).toLocaleString('en-IN')}</span>
              </div>
            )}
            {(stats?.subscriptions?.revenueThisMonth > 0) && (
              <div className="flex justify-between">
                <span className="text-surface-500 text-sm">Revenue (this month)</span>
                <span className="font-medium text-green-600" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{((stats.subscriptions.revenueThisMonth) / 100).toLocaleString('en-IN')}</span>
              </div>
            )}
            {(stats?.subscriptions?.lostRevenue > 0) && (
              <div className="flex justify-between">
                <span className="text-surface-500 text-sm">Lost Revenue (this month)</span>
                <span className="font-medium text-expense-600" style={{ fontVariantNumeric: 'tabular-nums' }}>-₹{((stats.subscriptions.lostRevenue) / 100).toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Users Tab
function UsersTab() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subStatusFilter, setSubStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [txnRange, setTxnRange] = useState('');
  const [joinedFrom, setJoinedFrom] = useState('');
  const [joinedTo, setJoinedTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [extendModal, setExtendModal] = useState({ show: false, user: null });
  const [extendData, setExtendData] = useState({ days: 10, reason: '' });
  const [extendLoading, setExtendLoading] = useState(false);
  // Status toggle confirmation modal
  const [statusModal, setStatusModal] = useState({ show: false, user: null });
  const [statusLoading, setStatusLoading] = useState(false);
  // Delete user confirmation modal
  const [deleteModal, setDeleteModal] = useState({ show: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  // User detail modal
  const [detailModal, setDetailModal] = useState({ show: false, user: null });
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentLinkModal, setPaymentLinkModal] = useState({ show: false, user: null });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, statusFilter, subStatusFilter, sortBy, sortOrder]);

  const fetchUsers = async (overrides = {}) => {
    try {
      setLoading(true);
      const curTxnRange = overrides.txnRange !== undefined ? overrides.txnRange : txnRange;
      const curJoinedFrom = overrides.joinedFrom !== undefined ? overrides.joinedFrom : joinedFrom;
      const curJoinedTo = overrides.joinedTo !== undefined ? overrides.joinedTo : joinedTo;
      const params = { page: overrides.page || pagination.page, limit: 20, sortBy, sortOrder };
      if (statusFilter) params.status = statusFilter;
      if (subStatusFilter) params.subStatus = subStatusFilter;
      if (search) params.search = search;
      if (curTxnRange) params.txnRange = curTxnRange;
      if (curJoinedFrom) params.joinedFrom = curJoinedFrom;
      if (curJoinedTo) params.joinedTo = curJoinedTo;

      const { data } = await adminAPI.getUsers(params);
      setUsers(data.data.users);
      setPagination(data.data.pagination);
      setSmsEnabled(data.data.smsEnabled || false);
    } catch (err) {
      setError(err.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  // Open status toggle confirmation modal
  const openStatusModal = (user) => {
    setStatusModal({ show: true, user });
  };

  // Confirm status toggle
  const handleConfirmStatusToggle = async () => {
    if (!statusModal.user) return;
    try {
      setStatusLoading(true);
      await adminAPI.updateUserStatus(statusModal.user._id, !statusModal.user.isActive);
      toast.success(`User ${statusModal.user.isActive ? 'deactivated' : 'activated'} successfully`);
      setStatusModal({ show: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.error || 'Failed to update user status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Open delete user confirmation modal
  const openDeleteModal = (user) => {
    setDeleteModal({ show: true, user });
  };

  // Confirm delete user
  const handleConfirmDelete = async () => {
    if (!deleteModal.user) return;
    try {
      setDeleteLoading(true);
      await adminAPI.deleteUser(deleteModal.user._id);
      toast.success(`User ${deleteModal.user.name} deleted successfully`);
      setDeleteModal({ show: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.error || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Open user detail modal
  const openDetailModal = async (user) => {
    setDetailModal({ show: true, user });
    setDetailData(null);
    setDetailLoading(true);
    try {
      const { data } = await adminAPI.getUserDetail(user._id);
      setDetailData(data.data);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch user details');
      setDetailModal({ show: false, user: null });
    } finally {
      setDetailLoading(false);
    }
  };

  // Calculate trial days remaining
  const getTrialDaysLeft = (subscription) => {
    if (!subscription || subscription.status !== 'trial' || !subscription.trialEndDate) return null;
    const now = new Date();
    const endDate = new Date(subscription.trialEndDate);
    const diffMs = endDate - now;
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  // Get subscription status color and label
  const getSubscriptionBadge = (subscription) => {
    if (!subscription) return { color: 'bg-surface-100 text-surface-600', label: 'None' };
    switch (subscription.status) {
      case 'trial':
        const daysLeft = getTrialDaysLeft(subscription);
        return {
          color: daysLeft <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700',
          label: `Trial (${daysLeft}d left)`
        };
      case 'active': return { color: 'bg-green-100 text-green-700', label: 'Active' };
      case 'paused': return { color: 'bg-blue-100 text-blue-700', label: 'Paused' };
      case 'cancelled': return { color: 'bg-red-100 text-red-700', label: 'Cancelled' };
      case 'expired': return { color: 'bg-surface-100 text-surface-600', label: 'Expired' };
      default: return { color: 'bg-surface-100 text-surface-600', label: subscription.status };
    }
  };

  const handleSendPaymentLink = async () => {
    if (!paymentLinkModal.user) return;
    try {
      const { data } = await adminSubscriptionsAPI.sendPaymentLink(paymentLinkModal.user._id);
      toast.success(data.message || 'Payment link sent!');
      setPaymentLinkModal({ show: false, user: null });
    } catch (err) {
      toast.error(err.error || 'Failed to send payment link');
    }
  };

  const openExtendModal = (user) => {
    setExtendModal({ show: true, user });
    setExtendData({ days: 10, reason: '' });
  };

  const handleExtend = async () => {
    if (!extendModal.user || !extendData.days || extendData.days <= 0) return;
    setExtendLoading(true);
    try {
      await adminSubscriptionsAPI.extendSubscription(
        extendModal.user._id,
        parseInt(extendData.days),
        extendData.reason
      );
      toast.success(`Extended subscription by ${extendData.days} days`);
      setExtendModal({ show: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.error || 'Failed to extend subscription');
    } finally {
      setExtendLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-semibold text-surface-900 mb-6">User Management</h2>

      {(success || error) && (
        <div className="mb-6">
          {success && <Alert variant="success" message={success} onClose={() => setSuccess('')} />}
          {error && <Alert variant="error" message={error} onClose={() => setError('')} />}
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-2.5 mb-6">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-theme-500/30 focus:border-theme-500"
            />
          </form>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-500/30"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={subStatusFilter}
            onChange={(e) => { setSubStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-500/30"
          >
            <option value="">All Plans</option>
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-500/30"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="lastActive-desc">Recently Active</option>
            <option value="lastActive-asc">Least Recently Active</option>
            <option value="transactionCount-desc">Most Txns</option>
            <option value="transactionCount-asc">Least Txns</option>
            {smsEnabled && <option value="smsCount-desc">Most SMS</option>}
            {smsEnabled && <option value="smsCount-asc">Least SMS</option>}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <select
            value={txnRange}
            onChange={(e) => setTxnRange(e.target.value)}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-500/30"
          >
            <option value="">All Transactions</option>
            <option value="0">0 transactions</option>
            <option value="1-25">1 – 25</option>
            <option value="25-50">25 – 50</option>
            <option value="50-75">50 – 75</option>
            <option value="75-100">75 – 100</option>
            <option value="100plus">100+</option>
          </select>
          <div className="flex items-center gap-2">
            <div className="flex-1 sm:flex-none sm:w-[160px]">
              <DateTimeInput
                value={joinedFrom}
                onChange={(val) => setJoinedFrom(val)}
                dateOnly
                placeholder="From date"
                className="!py-2 text-sm"
              />
            </div>
            <span className="text-surface-400 text-xs flex-shrink-0">to</span>
            <div className="flex-1 sm:flex-none sm:w-[160px]">
              <DateTimeInput
                value={joinedTo}
                onChange={(val) => setJoinedTo(val)}
                dateOnly
                placeholder="To date"
                className="!py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); fetchUsers({ page: 1 }); }}
              className="px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
            >
              Apply
            </button>
            {(txnRange || joinedFrom || joinedTo) && (
              <button
                onClick={() => { setTxnRange(''); setJoinedFrom(''); setJoinedTo(''); setPagination(prev => ({ ...prev, page: 1 })); fetchUsers({ txnRange: '', joinedFrom: '', joinedTo: '', page: 1 }); }}
                className="px-3 py-2.5 text-xs font-medium text-surface-500 hover:text-surface-700 hover:bg-surface-50 border border-surface-200 rounded-xl"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-surface-500">No users found</div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="xl:hidden space-y-4">
            {users.map((user) => {
              const subBadge = getSubscriptionBadge(user.subscription);
              const trialDays = getTrialDaysLeft(user.subscription);
              return (
                <div key={user._id} className="border border-surface-200 rounded-xl p-4 space-y-3">
                  {/* User Info Row */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-surface-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span
                        className="text-surface-600 font-medium"
                        style={{ display: user.avatar ? 'none' : 'flex' }}
                      >
                        {user.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-surface-900 truncate">{user.name}</div>
                      <div className="text-sm text-surface-500 truncate">{user.email}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                      user.isActive ? 'bg-income-100 text-income-700' : 'bg-expense-100 text-expense-700'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Subscription & Stats Row */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${subBadge.color}`}>
                      <Crown className="w-3 h-3" />
                      {subBadge.label}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Receipt className="w-3 h-3" />
                      {user.transactionCount || 0} txns
                    </span>
                    {smsEnabled && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <MessageCircle className="w-3 h-3" />
                        {user.smsCount || 0} SMS
                      </span>
                    )}
                    {user.registrationPlatform && user.registrationPlatform !== 'web' && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.registrationPlatform === 'ios' ? 'bg-gray-100 text-gray-700'
                        : user.registrationPlatform === 'android' ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                      }`}>
                        {user.registrationPlatform === 'ios' ? '🍎' : user.registrationPlatform === 'android' ? '🤖' : '🌐'}
                        {user.registrationPlatform}
                      </span>
                    )}
                    <span className="text-xs text-surface-500">
                      Joined: {formatAdminDate(user.createdAt)}
                    </span>
                    {(user.lastActive || user.lastLogin) && (
                      <span className="text-xs text-surface-400">
                        Active: {formatAdminDate(user.lastActive || user.lastLogin)}
                      </span>
                    )}
                  </div>

                  {/* Tutorial Status */}
                  <div className="text-xs">
                    {user.onboardingStatus === 'completed' ? (
                      <span className="text-income-600">Tutorial completed</span>
                    ) : user.onboardingStatus === 'skipped' ? (
                      <span className="text-amber-600">Tutorial skipped at step {(user.onboardingSkippedAtStep || 0) + 1}/6</span>
                    ) : (
                      <span className="text-surface-400">Tutorial not started</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-surface-100">
                    <button
                      onClick={() => openDetailModal(user)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-blue-50 text-blue-700 active:bg-blue-100"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    {user.subscription && (trialDays !== null || ['active', 'expired'].includes(user.subscription.status)) && (
                      <button
                        onClick={() => openExtendModal(user)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-green-50 text-green-700 active:bg-green-100"
                      >
                        <Play className="w-3 h-3" /> Extend
                      </button>
                    )}
                    <button
                      onClick={() => setPaymentLinkModal({ show: true, user })}
                      className="p-2 rounded-lg bg-blue-50 text-blue-700 active:bg-blue-100"
                      title="Send Payment Link"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => openStatusModal(user)}
                      disabled={statusLoading && statusModal.user?._id === user._id}
                      className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                        user.isActive
                          ? 'bg-amber-50 text-amber-700 active:bg-amber-100'
                          : 'bg-income-50 text-income-700 active:bg-income-100'
                      }`}
                      title={user.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {statusLoading && statusModal.user?._id === user._id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : user.isActive ? (
                        <UserX className="w-3.5 h-3.5" />
                      ) : (
                        <UserCheck className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      onClick={() => openDeleteModal(user)}
                      disabled={deleteLoading && deleteModal.user?._id === user._id}
                      className="p-2 rounded-lg bg-red-50 text-red-700 active:bg-red-100 disabled:opacity-50"
                      title="Delete User"
                    >
                      {deleteLoading && deleteModal.user?._id === user._id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 font-medium text-surface-600">User</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Subscription</th>
                  <th className="text-center py-3 px-4 font-medium text-surface-600">Txns</th>
                  {smsEnabled && <th className="text-center py-3 px-4 font-medium text-surface-600">SMS</th>}
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Joined</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Last Active</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Platform</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const subBadge = getSubscriptionBadge(user.subscription);
                  const trialDays = getTrialDaysLeft(user.subscription);
                  return (
                    <tr key={user._id} className="border-b border-surface-100 hover:bg-surface-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-surface-200 flex items-center justify-center overflow-hidden">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span
                              className="text-surface-600 font-medium"
                              style={{ display: user.avatar ? 'none' : 'flex' }}
                            >
                              {user.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-surface-900">{user.name}</div>
                            <div className="text-sm text-surface-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${subBadge.color}`}>
                          <Crown className="w-3 h-3" />
                          {subBadge.label}
                        </span>
                        {trialDays !== null && trialDays <= 5 && (
                          <div className="text-xs text-amber-600 mt-1">Expires soon</div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {user.transactionCount || 0}
                        </span>
                      </td>
                      {smsEnabled && (
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            (user.smsCount || 0) > 0 ? 'bg-purple-100 text-purple-700' : 'bg-surface-100 text-surface-500'
                          }`}>
                            {user.smsCount || 0}
                          </span>
                        </td>
                      )}
                      <td className="py-4 px-4 text-surface-600">
                        {formatAdminDate(user.createdAt)}
                      </td>
                      <td className="py-4 px-4 text-surface-600">
                        <div>{user.lastActive ? formatAdminDate(user.lastActive) : (user.lastLogin ? formatAdminDate(user.lastLogin) : 'Never')}</div>
                        {user.lastLogin && (
                          <div className="text-[11px] text-surface-400">Login: {formatAdminDate(user.lastLogin)}</div>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          user.registrationPlatform === 'ios' ? 'bg-gray-100 text-gray-700'
                          : user.registrationPlatform === 'android' ? 'bg-green-100 text-green-700'
                          : user.registrationPlatform === 'marketing' ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                        }`}>
                          {user.registrationPlatform === 'ios' ? '🍎' : user.registrationPlatform === 'android' ? '🤖' : user.registrationPlatform === 'marketing' ? '🌐' : '💻'}
                          {user.registrationPlatform || 'web'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.isActive
                            ? 'bg-income-100 text-income-700'
                            : 'bg-expense-100 text-expense-700'
                        }`}>
                          {user.isActive ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDetailModal(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View User Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {user.subscription && (trialDays !== null || ['active', 'expired'].includes(user.subscription.status)) && (
                            <button
                              onClick={() => openExtendModal(user)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Extend Subscription"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setPaymentLinkModal({ show: true, user })}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Send Payment Link"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openStatusModal(user)}
                            disabled={statusLoading && statusModal.user?._id === user._id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              user.isActive
                                ? 'text-amber-600 hover:bg-amber-50'
                                : 'text-income-600 hover:bg-income-50'
                            }`}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {statusLoading && statusModal.user?._id === user._id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : user.isActive ? (
                              <UserX className="w-4 h-4" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openDeleteModal(user)}
                            disabled={deleteLoading && deleteModal.user?._id === user._id}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete User"
                          >
                            {deleteLoading && deleteModal.user?._id === user._id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
          <span className="text-sm text-surface-600">
            Page {pagination.page} of {pagination.pages} ({pagination.total} users)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {extendModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">Extend Subscription</h2>
              <button
                onClick={() => setExtendModal({ show: false, user: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-50 rounded-xl">
                <p className="text-sm text-surface-600">
                  User: <span className="font-medium text-surface-900">{extendModal.user?.name}</span>
                </p>
                <p className="text-sm text-surface-500">{extendModal.user?.email}</p>
                {extendModal.user?.subscription && (
                  <p className="text-sm text-surface-500 mt-2">
                    Current status: <span className="font-medium capitalize">{extendModal.user.subscription.status}</span>
                    {extendModal.user.subscription.status === 'trial' && extendModal.user.subscription.trialEndDate && (
                      <span className="ml-1">
                        (ends {formatAdminDate(extendModal.user.subscription.trialEndDate)})
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Add Days</label>
                <input
                  type="number"
                  value={extendData.days}
                  onChange={(e) => setExtendData(prev => ({ ...prev, days: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min="1"
                />
                <p className="text-xs text-surface-500 mt-1">Days will be added to the current end date. Use large values (e.g. 99999) for lifetime access.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Reason (optional)</label>
                <textarea
                  value={extendData.reason}
                  onChange={(e) => setExtendData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Internal note..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setExtendModal({ show: false, user: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={extendLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleExtend}
                disabled={extendLoading || !extendData.days || extendData.days <= 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {extendLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Extend by {extendData.days} days
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Toggle Confirmation Modal */}
      {statusModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">
                {statusModal.user?.isActive ? 'Deactivate User' : 'Activate User'}
              </h2>
              <button
                onClick={() => setStatusModal({ show: false, user: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-50 rounded-xl">
                <p className="text-sm text-surface-600">
                  User: <span className="font-medium text-surface-900">{statusModal.user?.name}</span>
                </p>
                <p className="text-sm text-surface-500">{statusModal.user?.email}</p>
              </div>
              <p className="text-surface-600">
                Are you sure you want to {statusModal.user?.isActive ? 'deactivate' : 'activate'} this user?
                {statusModal.user?.isActive && (
                  <span className="block mt-2 text-sm text-surface-500">
                    The user will not be able to log in while deactivated.
                  </span>
                )}
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setStatusModal({ show: false, user: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={statusLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusToggle}
                disabled={statusLoading}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-xl font-medium disabled:opacity-50 ${
                  statusModal.user?.isActive
                    ? 'bg-expense-600 hover:bg-expense-700'
                    : 'bg-income-600 hover:bg-income-700'
                }`}
              >
                {statusLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : statusModal.user?.isActive ? (
                  <UserX className="w-4 h-4" />
                ) : (
                  <UserCheck className="w-4 h-4" />
                )}
                {statusModal.user?.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-red-600">Delete User</h2>
              <button
                onClick={() => setDeleteModal({ show: false, user: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                <p className="text-sm text-surface-600">
                  User: <span className="font-medium text-surface-900">{deleteModal.user?.name}</span>
                </p>
                <p className="text-sm text-surface-500">{deleteModal.user?.email}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-100">
                <p className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Warning: This action cannot be undone!
                </p>
                <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                  <li>All transactions will be deleted</li>
                  <li>All recurring transactions will be deleted</li>
                  <li>All accounts and categories will be deleted</li>
                  <li>Active subscription will be cancelled</li>
                  <li>Profile images and attachments will be removed</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setDeleteModal({ show: false, user: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Payment Link Confirmation */}
      <ConfirmModal
        isOpen={paymentLinkModal.show}
        onClose={() => setPaymentLinkModal({ show: false, user: null })}
        onConfirm={handleSendPaymentLink}
        title="Send Payment Link"
        message={`Send a payment link email to ${paymentLinkModal.user?.name} (${paymentLinkModal.user?.email})?`}
        confirmText="Send Link"
        variant="primary"
      />

      {/* User Detail Modal */}
      {detailModal.show && (
        <div className="modal-overlay" onClick={() => setDetailModal({ show: false, user: null })}>
          <div className="modal-content max-w-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 sm:py-4 sm:px-6 border-b border-surface-100 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-semibold text-surface-900">User Details</h2>
              <button
                onClick={() => setDetailModal({ show: false, user: null })}
                className="p-2 -mr-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-4 sm:space-y-5">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
                </div>
              ) : detailData ? (
                <>
                  {/* Section 1 - Profile Header */}
                  <div className="flex items-center gap-3 pb-3 border-b border-surface-100">
                    <div className="w-11 h-11 rounded-full bg-surface-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {detailData.user.avatar ? (
                        <img src={detailData.user.avatar} alt="" className="w-11 h-11 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <span className="text-base text-surface-600 font-semibold" style={{ display: detailData.user.avatar ? 'none' : 'flex' }}>
                        {detailData.user.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-surface-900 leading-tight">{detailData.user.name}</div>
                      <div className="text-xs text-surface-500 truncate">{detailData.user.email}</div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0 ${detailData.user.isActive ? 'bg-income-100 text-income-700' : 'bg-expense-100 text-expense-700'}`}>
                      {detailData.user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Section 1b - Profile Details */}
                  <div className="border border-surface-100 rounded-xl overflow-hidden divide-y divide-surface-100 text-sm">
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-surface-500">Signed up via</span>
                      <span className="font-medium text-surface-900">{detailData.user.authProvider === 'google' ? 'Google' : 'Email'}</span>
                    </div>
                    {detailData.user.phoneNumber && (
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-surface-500">Phone</span>
                        <span className="font-medium text-surface-900">
                          {detailData.user.phoneNumber}
                          {detailData.user.phoneVerified && <span className="ml-1.5 text-[11px] text-income-600 font-normal">Verified</span>}
                        </span>
                      </div>
                    )}
                    {(detailData.user.country || detailData.user.city || detailData.user.state) && (
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-surface-500">Location</span>
                        <span className="font-medium text-surface-900 text-right">
                          {[detailData.user.city, detailData.user.state, detailData.user.country ? (getCountryByCode(detailData.user.country)?.name || detailData.user.country) : null].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {detailData.user.panNumber && (
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-surface-500">PAN</span>
                        <span className="font-medium text-surface-900">{detailData.user.panNumber}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-surface-500">Onboarding</span>
                      <span className="font-medium text-surface-900 capitalize">{detailData.user.onboardingStatus || 'not started'}</span>
                    </div>
                    {detailData.user.registrationIp && (
                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-surface-500">Reg. IP</span>
                        <span className="font-medium text-surface-900 font-mono text-xs">{detailData.user.registrationIp}</span>
                      </div>
                    )}
                    {detailData.user.registrationUserAgent && (
                      <div className="px-4 py-2.5">
                        <span className="text-surface-500">Reg. Device</span>
                        <p className="text-surface-700 text-xs mt-1 break-all leading-relaxed">{detailData.user.registrationUserAgent}</p>
                      </div>
                    )}
                  </div>

                  {/* Section 2 - Account Stats */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="bg-surface-50 rounded-xl p-3 text-center">
                      <div className="text-[10px] sm:text-xs text-surface-400 uppercase tracking-wide">Joined</div>
                      <div className="font-semibold text-sm sm:text-base text-surface-900 mt-1">{formatAdminDate(detailData.user.createdAt)}</div>
                    </div>
                    <div className="bg-surface-50 rounded-xl p-3 text-center">
                      <div className="text-[10px] sm:text-xs text-surface-400 uppercase tracking-wide">Last Active</div>
                      <div className="font-semibold text-sm sm:text-base text-surface-900 mt-1">{detailData.user.lastActive ? formatAdminDate(detailData.user.lastActive) : 'Never'}</div>
                    </div>
                    <div className="bg-surface-50 rounded-xl p-3 text-center">
                      <div className="text-[10px] sm:text-xs text-surface-400 uppercase tracking-wide">Last Login</div>
                      <div className="font-semibold text-sm sm:text-base text-surface-900 mt-1">{detailData.user.lastLogin ? formatAdminDate(detailData.user.lastLogin) : 'Never'}</div>
                    </div>
                    <div className="bg-surface-50 rounded-xl p-3 text-center">
                      <div className="text-[10px] sm:text-xs text-surface-400 uppercase tracking-wide">Transactions</div>
                      <div className="font-semibold text-sm sm:text-base text-surface-900 mt-1">{detailData.transactionCount}</div>
                    </div>
                  </div>

                  {/* Section 3 - Subscription */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider px-1">Subscription</h3>
                    {detailData.subscription ? (() => {
                      const sub = detailData.subscription;
                      const badge = getSubscriptionBadge(sub);
                      const now = new Date();
                      const endDate = sub.status === 'trial' ? sub.trialEndDate : sub.currentPeriodEnd;
                      const daysRemaining = endDate ? Math.max(0, Math.ceil((new Date(endDate) - now) / (1000 * 60 * 60 * 24))) : 0;
                      return (
                        <div className="bg-surface-50 rounded-xl p-4 space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-surface-500">Status:</span>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                              <Crown className="w-3 h-3" /> {badge.label}
                            </span>
                            <span className="text-surface-500 ml-auto">{daysRemaining} days remaining</span>
                          </div>
                          {sub.status === 'trial' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><span className="text-surface-500">Trial Start:</span> <span className="font-medium">{formatAdminDate(sub.trialStartDate)}</span></div>
                              <div><span className="text-surface-500">Trial End:</span> <span className="font-medium">{formatAdminDate(sub.trialEndDate)}</span></div>
                            </div>
                          )}
                          {sub.status === 'active' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><span className="text-surface-500">Start:</span> <span className="font-medium">{formatAdminDate(sub.subscriptionStartDate)}</span></div>
                              <div><span className="text-surface-500">Period End:</span> <span className="font-medium">{formatAdminDate(sub.currentPeriodEnd)}</span></div>
                            </div>
                          )}
                          {sub.billingInterval && sub.status !== 'trial' && (
                            <div><span className="text-surface-500">Billing:</span> <span className="font-medium capitalize">{sub.billingInterval}</span>{sub.amount > 0 && <span className="ml-1">(₹{(sub.amount / 100).toFixed(2)})</span>}</div>
                          )}
                          {sub.couponApplied && (
                            <div><span className="text-surface-500">Coupon:</span> <span className="font-medium">{sub.couponApplied.code}</span></div>
                          )}
                          {sub.cancelReason && (
                            <div><span className="text-surface-500">Cancel Reason:</span> <span className="font-medium">{sub.cancelReason}</span></div>
                          )}
                          {sub.willCancelAt && (
                            <div><span className="text-surface-500">Will Cancel At:</span> <span className="font-medium">{formatAdminDate(sub.willCancelAt)}</span></div>
                          )}
                        </div>
                      );
                    })() : (
                      <p className="text-sm text-surface-500">No subscription found</p>
                    )}
                  </div>

                  {/* Section 4 - Extension History */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider px-1">Extension History</h3>
                    {detailData.subscription?.extensionHistory?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-surface-200">
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Date</th>
                              <th className="text-center py-2 px-2 font-medium text-surface-600">Days</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Reason</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">By</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Previous → New</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.subscription.extensionHistory.map((ext, idx) => (
                              <tr key={idx} className="border-b border-surface-100">
                                <td className="py-2 px-2 text-surface-600">{formatAdminDate(ext.createdAt)}</td>
                                <td className="py-2 px-2 text-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">+{ext.days}d</span>
                                </td>
                                <td className="py-2 px-2 text-surface-600">{ext.reason || '-'}</td>
                                <td className="py-2 px-2 text-surface-600">{ext.extendedBy?.name || '-'}</td>
                                <td className="py-2 px-2 text-surface-600 text-xs">
                                  {formatAdminDate(ext.previousEnd)} → {formatAdminDate(ext.newEnd)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-surface-500">No extensions</p>
                    )}
                  </div>

                  {/* Section 5 - FCM Tokens */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider px-1">FCM Tokens</h3>
                    {detailData.user?.fcmTokens?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-surface-200">
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Platform</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Device ID</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Token</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Added</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Last Used</th>
                              <th className="text-center py-2 px-2 font-medium text-surface-600"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.user.fcmTokens.map((tok) => (
                              <tr key={tok.deviceId || tok._id} className="border-b border-surface-100">
                                <td className="py-2 px-2 text-surface-600 capitalize">{tok.platform || '-'}</td>
                                <td className="py-2 px-2 text-surface-600 text-xs font-mono">{tok.deviceId ? tok.deviceId.substring(0, 20) + '...' : '-'}</td>
                                <td className="py-2 px-2 text-surface-600 text-xs font-mono">{tok.token ? tok.token.substring(0, 20) + '...' : '-'}</td>
                                <td className="py-2 px-2 text-surface-600">{tok.createdAt ? formatAdminDate(tok.createdAt) : '-'}</td>
                                <td className="py-2 px-2 text-surface-600">{tok.lastUsedAt ? formatAdminDate(tok.lastUsedAt) : '-'}</td>
                                <td className="py-2 px-2 text-center">
                                  <button
                                    onClick={async () => {
                                      try {
                                        await adminAPI.deleteUserFcmToken(detailData.user._id, tok.deviceId);
                                        toast.success('FCM token deleted');
                                        const { data } = await adminAPI.getUserDetail(detailData.user._id);
                                        setDetailData(data.data);
                                      } catch (err) {
                                        toast.error(err.error || 'Failed to delete token');
                                      }
                                    }}
                                    className="p-1 text-surface-400 hover:text-expense-600 rounded"
                                    title="Delete token"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-surface-500">No push tokens</p>
                    )}
                  </div>

                  {/* Payment History */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-surface-400 uppercase tracking-wider px-1">Payment History</h3>
                    {detailData.payments?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-surface-200">
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Date</th>
                              <th className="text-right py-2 px-2 font-medium text-surface-600">Amount</th>
                              <th className="text-center py-2 px-2 font-medium text-surface-600">Status</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Method</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Type</th>
                              <th className="text-left py-2 px-2 font-medium text-surface-600">Coupon</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.payments.map((pmt) => (
                              <tr key={pmt._id} className="border-b border-surface-100">
                                <td className="py-2 px-2 text-surface-600">{formatAdminDate(pmt.createdAt)}</td>
                                <td className="py-2 px-2 text-right font-medium">₹{(pmt.amount / 100).toFixed(2)}</td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    pmt.status === 'success' ? 'bg-green-100 text-green-700' :
                                    pmt.status === 'failed' ? 'bg-red-100 text-red-700' :
                                    pmt.status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {pmt.status}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-surface-600 capitalize">{pmt.method || '-'}</td>
                                <td className="py-2 px-2 text-surface-600 capitalize">{pmt.paymentType || '-'}</td>
                                <td className="py-2 px-2 text-surface-600">{pmt.couponApplied?.code || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-surface-500">No payments</p>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Leads Tab
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [replyModal, setReplyModal] = useState({ show: false, lead: null });
  const [replyData, setReplyData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [viewModal, setViewModal] = useState({ show: false, lead: null });

  useEffect(() => {
    fetchLeads();
  }, [pagination.page, statusFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;

      const { data } = await adminAPI.getLeads(params);
      setLeads(data.data.leads);
      setPagination(data.data.pagination);
    } catch (err) {
      setError(err.error || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      await adminAPI.updateLead(leadId, { status });
      setSuccess('Lead status updated');
      fetchLeads();
    } catch (err) {
      setError(err.error || 'Failed to update lead');
    }
  };

  const openReplyModal = (lead) => {
    setReplyModal({ show: true, lead });
    setReplyData({ subject: `Re: ${lead.subject || 'Your inquiry'}`, message: '' });
  };

  const handleSendReply = async () => {
    if (!replyData.subject || !replyData.message) {
      setError('Subject and message are required');
      return;
    }

    try {
      setSending(true);
      await adminAPI.replyToLead(replyModal.lead._id, replyData.subject, replyData.message);
      setSuccess('Reply sent successfully');
      setReplyModal({ show: false, lead: null });
      setReplyData({ subject: '', message: '' });
      fetchLeads();
    } catch (err) {
      setError(err.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-amber-100 text-amber-700';
      case 'contacted': return 'bg-blue-100 text-blue-700';
      case 'converted': return 'bg-income-100 text-income-700';
      case 'closed': return 'bg-surface-100 text-surface-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-semibold text-surface-900 mb-6">Lead Management</h2>

      {(success || error) && (
        <div className="mb-6">
          {success && <Alert variant="success" message={success} onClose={() => setSuccess('')} />}
          {error && <Alert variant="error" message={error} onClose={() => setError('')} />}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Leads List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-12 text-surface-500">No leads found</div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="xl:hidden space-y-4">
            {leads.map((lead) => (
              <div key={lead._id} className="border border-surface-200 rounded-xl p-4 space-y-3">
                {/* Lead Info & Status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-surface-900">{lead.name}</div>
                    <div className="text-sm text-surface-500 truncate">{lead.email}</div>
                    {lead.phone && <div className="text-sm text-surface-400">{lead.phone}</div>}
                  </div>
                  <select
                    value={lead.status}
                    onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer flex-shrink-0 ${getStatusColor(lead.status)}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                {/* Source & Date */}
                <div className="flex items-center justify-between text-xs text-surface-500">
                  <span>Source: {lead.source || 'Website'}</span>
                  <span>{formatAdminDate(lead.createdAt)}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-surface-100">
                  <button
                    onClick={() => setViewModal({ show: true, lead })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200"
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => openReplyModal(lead)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <Send className="w-4 h-4" /> Reply
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Lead</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Source</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead._id} className="border-b border-surface-100 hover:bg-surface-50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-surface-900">{lead.name}</div>
                      <div className="text-sm text-surface-500">{lead.email}</div>
                      {lead.phone && <div className="text-sm text-surface-400">{lead.phone}</div>}
                    </td>
                    <td className="py-4 px-4 text-surface-600">{lead.source || 'Website'}</td>
                    <td className="py-4 px-4 text-surface-600">
                      {formatAdminDate(lead.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(lead.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setViewModal({ show: true, lead })}
                          className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReplyModal(lead)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          <Send className="w-4 h-4" /> Reply
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
          <span className="text-sm text-surface-600">
            Page {pagination.page} of {pagination.pages} ({pagination.total} leads)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Lead Modal */}
      {viewModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">Lead Details</h2>
              <button
                onClick={() => setViewModal({ show: false, lead: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-surface-500">Name</label>
                  <p className="text-surface-900">{viewModal.lead?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-surface-500">Email</label>
                  <p className="text-surface-900">{viewModal.lead?.email}</p>
                </div>
                {viewModal.lead?.phone && (
                  <div>
                    <label className="text-sm font-medium text-surface-500">Phone</label>
                    <p className="text-surface-900">{viewModal.lead?.phone}</p>
                  </div>
                )}
                {viewModal.lead?.company && (
                  <div>
                    <label className="text-sm font-medium text-surface-500">Company</label>
                    <p className="text-surface-900">{viewModal.lead?.company}</p>
                  </div>
                )}
              </div>

              {/* Customer Message */}
              {viewModal.lead?.message && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-sm font-medium text-surface-500 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Customer Message
                  </label>
                  <div className="mt-2 p-3 bg-surface-50 rounded-xl">
                    <p className="text-surface-900 whitespace-pre-wrap">{viewModal.lead?.message}</p>
                    <p className="text-xs text-surface-400 mt-2">
                      {new Date(viewModal.lead?.createdAt).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              )}

              {/* Admin Replies */}
              {viewModal.lead?.replies && viewModal.lead.replies.length > 0 && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-sm font-medium text-surface-500 flex items-center gap-2 mb-3">
                    <Send className="w-4 h-4" /> Admin Replies ({viewModal.lead.replies.length})
                  </label>
                  <div className="space-y-3">
                    {viewModal.lead.replies.map((reply, index) => (
                      <div key={index} className="p-3 bg-blue-50 rounded-xl border-l-4 border-blue-400">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-blue-900 text-sm">{reply.subject}</p>
                          <span className="text-xs text-blue-600">{reply.sentBy}</span>
                        </div>
                        <p className="text-blue-800 whitespace-pre-wrap text-sm">{reply.message}</p>
                        <p className="text-xs text-blue-500 mt-2">
                          {new Date(reply.sentAt).toLocaleString('en-IN')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {viewModal.lead?.notes && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-sm font-medium text-surface-500">Internal Notes</label>
                  <p className="text-surface-600 whitespace-pre-wrap text-sm mt-1">{viewModal.lead?.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {replyModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">Reply to Lead</h2>
              <button
                onClick={() => setReplyModal({ show: false, lead: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-50 rounded-xl">
                <p className="text-sm text-surface-600">
                  Replying to: <span className="font-medium text-surface-900">{replyModal.lead?.name}</span> ({replyModal.lead?.email})
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={replyData.subject}
                  onChange={(e) => setReplyData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Message</label>
                <textarea
                  value={replyData.message}
                  onChange={(e) => setReplyData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600 resize-none"
                  placeholder="Type your reply..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setReplyModal({ show: false, lead: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={sending || !replyData.subject || !replyData.message}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Feedback Tab
function FeedbackTab() {
  const { toast } = useToast();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [replyModal, setReplyModal] = useState({ show: false, feedback: null });
  const [replyData, setReplyData] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [viewModal, setViewModal] = useState({ show: false, feedback: null });

  useEffect(() => {
    fetchFeedback();
  }, [pagination.page, statusFilter, typeFilter]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      const { data } = await adminAPI.getFeedback(params);
      setFeedbacks(data.data.feedbacks);
      setPagination(data.data.pagination);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (feedbackId, status) => {
    try {
      await adminAPI.updateFeedback(feedbackId, { status });
      toast.success('Feedback status updated');
      fetchFeedback();
    } catch (err) {
      toast.error(err.error || 'Failed to update feedback');
    }
  };

  const openReplyModal = (feedback) => {
    setReplyModal({ show: true, feedback });
    setReplyData({ subject: `Re: ${feedback.title || 'Your feedback'}`, message: '' });
  };

  const handleSendReply = async () => {
    if (!replyData.subject || !replyData.message) {
      toast.error('Subject and message are required');
      return;
    }

    try {
      setSending(true);
      await adminAPI.replyToFeedback(replyModal.feedback._id, replyData.subject, replyData.message);
      toast.success('Reply sent successfully');
      setReplyModal({ show: false, feedback: null });
      setReplyData({ subject: '', message: '' });
      fetchFeedback();
    } catch (err) {
      toast.error(err.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-amber-100 text-amber-700';
      case 'reviewed': return 'bg-blue-100 text-blue-700';
      case 'resolved': return 'bg-income-100 text-income-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'bug': return 'bg-expense-100 text-expense-700';
      case 'feature': return 'bg-purple-100 text-purple-700';
      case 'suggestion': return 'bg-blue-100 text-blue-700';
      case 'other': return 'bg-surface-100 text-surface-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-semibold text-surface-900 mb-6">Feedback Management</h2>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="flex-1 sm:flex-initial px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="flex-1 sm:flex-initial px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
        >
          <option value="">All Types</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature Request</option>
          <option value="suggestion">Suggestion</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-12 text-surface-500">No feedback found</div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="xl:hidden space-y-4">
            {feedbacks.map((feedback) => (
              <div key={feedback._id} className="border border-surface-200 rounded-xl p-4 space-y-3">
                {/* Title & Status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-surface-900 truncate">{feedback.title}</div>
                    <div className="text-sm text-surface-500 truncate">
                      {feedback.user?.name || feedback.userName || 'Anonymous'}
                    </div>
                  </div>
                  <select
                    value={feedback.status}
                    onChange={(e) => handleStatusChange(feedback._id, e.target.value)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer flex-shrink-0 ${getStatusColor(feedback.status)}`}
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                {/* Type & Date */}
                <div className="flex items-center justify-between text-xs">
                  <span className={`inline-block px-2.5 py-1 rounded-full font-medium ${getTypeColor(feedback.type)}`}>
                    {feedback.type?.charAt(0).toUpperCase() + feedback.type?.slice(1)}
                  </span>
                  <span className="text-surface-500">
                    {formatAdminDate(feedback.createdAt)}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-surface-100">
                  <button
                    onClick={() => setViewModal({ show: true, feedback })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200"
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => openReplyModal(feedback)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <Send className="w-4 h-4" /> Reply
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Feedback</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Type</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr key={feedback._id} className="border-b border-surface-100 hover:bg-surface-50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-surface-900">{feedback.title}</div>
                      <div className="text-sm text-surface-500">
                        {feedback.user?.name || feedback.userName || 'Anonymous'} - {feedback.user?.email || feedback.userEmail}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getTypeColor(feedback.type)}`}>
                        {feedback.type?.charAt(0).toUpperCase() + feedback.type?.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-surface-600">
                      {formatAdminDate(feedback.createdAt)}
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={feedback.status}
                        onChange={(e) => handleStatusChange(feedback._id, e.target.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(feedback.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setViewModal({ show: true, feedback })}
                          className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReplyModal(feedback)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          <Send className="w-4 h-4" /> Reply
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
          <span className="text-sm text-surface-600">
            Page {pagination.page} of {pagination.pages} ({pagination.total} feedbacks)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Feedback Modal */}
      {viewModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">Feedback Details</h2>
              <button
                onClick={() => setViewModal({ show: false, feedback: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Feedback Info */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-surface-900">{viewModal.feedback?.title}</h3>
                  <p className="text-sm text-surface-500">
                    {viewModal.feedback?.user?.name || viewModal.feedback?.userName || 'Anonymous'}
                    {' '}({viewModal.feedback?.user?.email || viewModal.feedback?.userEmail})
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getTypeColor(viewModal.feedback?.type)}`}>
                  {viewModal.feedback?.type?.charAt(0).toUpperCase() + viewModal.feedback?.type?.slice(1)}
                </span>
              </div>

              {/* Customer Message */}
              <div className="pt-4 border-t border-surface-200">
                <label className="text-sm font-medium text-surface-500 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Customer Message
                </label>
                <div className="mt-2 p-3 bg-surface-50 rounded-xl">
                  <p className="text-surface-900 whitespace-pre-wrap">{viewModal.feedback?.message}</p>
                  <p className="text-xs text-surface-400 mt-2">
                    {new Date(viewModal.feedback?.createdAt).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Admin Replies */}
              {viewModal.feedback?.replies && viewModal.feedback.replies.length > 0 && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-sm font-medium text-surface-500 flex items-center gap-2 mb-3">
                    <Send className="w-4 h-4" /> Admin Replies ({viewModal.feedback.replies.length})
                  </label>
                  <div className="space-y-3">
                    {viewModal.feedback.replies.map((reply, index) => (
                      <div key={index} className="p-3 bg-purple-50 rounded-xl border-l-4 border-purple-400">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-purple-900 text-sm">{reply.subject}</p>
                          <span className="text-xs text-purple-600">{reply.sentBy}</span>
                        </div>
                        <p className="text-purple-800 whitespace-pre-wrap text-sm">{reply.message}</p>
                        <p className="text-xs text-purple-500 mt-2">
                          {new Date(reply.sentAt).toLocaleString('en-IN')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewModal.feedback?.rating && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-sm font-medium text-surface-500">Rating</label>
                  <p className="text-surface-900">{viewModal.feedback?.rating}/5</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal - Feedback */}
      {replyModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">Reply to Feedback</h2>
              <button
                onClick={() => setReplyModal({ show: false, feedback: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-50 rounded-xl">
                <p className="text-sm text-surface-600">
                  Replying to: <span className="font-medium text-surface-900">
                    {replyModal.feedback?.user?.name || replyModal.feedback?.userName}
                  </span> ({replyModal.feedback?.user?.email || replyModal.feedback?.userEmail})
                </p>
                <p className="text-sm text-surface-500 mt-1">
                  Re: {replyModal.feedback?.title}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={replyData.subject}
                  onChange={(e) => setReplyData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Message</label>
                <textarea
                  value={replyData.message}
                  onChange={(e) => setReplyData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600 resize-none"
                  placeholder="Type your reply..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setReplyModal({ show: false, feedback: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={sending || !replyData.subject || !replyData.message}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reports Tab
function ReportsTab() {
  const { toast } = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [viewModal, setViewModal] = useState({ show: false, report: null });
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [pagination.page, statusFilter, typeFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      const { data } = await adminAPI.getReports(params);
      setReports(data.reports);
      setPagination(data.pagination);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await adminAPI.getReportStats();
      setStats(data.stats);
    } catch (err) {
      console.error('Failed to fetch report stats:', err);
    }
  };

  const handleStatusChange = async (reportId, status) => {
    try {
      setUpdating(reportId);
      await adminAPI.updateReport(reportId, { status });
      toast.success('Report status updated');
      fetchReports();
      fetchStats();
    } catch (err) {
      toast.error(err.error || 'Failed to update report');
    } finally {
      setUpdating(null);
    }
  };

  const handleActionChange = async (reportId, actionTaken) => {
    try {
      setUpdating(reportId);
      await adminAPI.updateReport(reportId, { actionTaken });
      toast.success('Action recorded');
      fetchReports();
    } catch (err) {
      toast.error(err.error || 'Failed to update action');
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleEmailBlock = async (reportId, currentBlocked) => {
    try {
      setUpdating(reportId);
      await adminAPI.toggleEmailBlock(reportId, !currentBlocked);
      toast.success(currentBlocked ? 'Emails unblocked' : 'Emails blocked');
      fetchReports();
    } catch (err) {
      toast.error(err.error || 'Failed to toggle email block');
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateNotes = async (reportId, adminNotes) => {
    try {
      await adminAPI.updateReport(reportId, { adminNotes });
      toast.success('Notes saved');
      fetchReports();
    } catch (err) {
      toast.error(err.error || 'Failed to save notes');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'investigating': return 'bg-blue-100 text-blue-700';
      case 'resolved': return 'bg-income-100 text-income-700';
      case 'dismissed': return 'bg-surface-100 text-surface-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'none': return 'bg-surface-100 text-surface-700';
      case 'warning_sent': return 'bg-amber-100 text-amber-700';
      case 'emails_blocked': return 'bg-orange-100 text-orange-700';
      case 'entry_deleted': return 'bg-expense-100 text-expense-700';
      case 'account_suspended': return 'bg-red-100 text-red-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Pending</span>
            </div>
            <p className="text-2xl font-bold text-amber-800">{stats.byStatus?.pending || 0}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 mb-1">
              <Search className="w-4 h-4" />
              <span className="text-sm font-medium">Investigating</span>
            </div>
            <p className="text-2xl font-bold text-blue-800">{stats.byStatus?.investigating || 0}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-2 text-green-700 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Resolved</span>
            </div>
            <p className="text-2xl font-bold text-green-800">{stats.byStatus?.resolved || 0}</p>
          </div>
          <div className="bg-surface-50 rounded-xl p-4 border border-surface-200">
            <div className="flex items-center gap-2 text-surface-700 mb-1">
              <Flag className="w-4 h-4" />
              <span className="text-sm font-medium">Total</span>
            </div>
            <p className="text-2xl font-bold text-surface-800">{stats.total || 0}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-surface-900 mb-6">Report Management</h2>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 sm:flex-initial px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="flex-1 sm:flex-initial px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
          >
            <option value="">All Types</option>
            <option value="receivable">Receivable</option>
            <option value="debt">Debt</option>
          </select>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-surface-500">
            <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-surface-300" />
            <p>No reports found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="xl:hidden space-y-4">
              {reports.map((report) => (
                <div key={report._id} className="border border-surface-200 rounded-xl p-4 space-y-3">
                  {/* Reporter & Status */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-surface-900 truncate">{report.reporterEmail}</div>
                      {report.reporterName && (
                        <div className="text-sm text-surface-500">{report.reporterName}</div>
                      )}
                    </div>
                    <select
                      value={report.status}
                      onChange={(e) => handleStatusChange(report._id, e.target.value)}
                      disabled={updating === report._id}
                      className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer flex-shrink-0 ${getStatusColor(report.status)} disabled:opacity-50`}
                    >
                      <option value="pending">Pending</option>
                      <option value="investigating">Investigating</option>
                      <option value="resolved">Resolved</option>
                      <option value="dismissed">Dismissed</option>
                    </select>
                  </div>

                  {/* Type & Amount */}
                  <div className="flex items-center justify-between">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                      report.type === 'receivable' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {report.type?.charAt(0).toUpperCase() + report.type?.slice(1)}
                    </span>
                    <span className="font-semibold text-surface-900">{formatAmount(report.reportedAmount)}</span>
                  </div>

                  {/* Owner Info */}
                  {report.reportedUser && (
                    <div className="text-sm text-surface-600">
                      Owner: {report.reportedUser.name} ({report.reportedUser.email})
                    </div>
                  )}

                  {/* Date & Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-surface-100">
                    <span className="text-xs text-surface-500">
                      {formatAdminDate(report.createdAt)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleEmailBlock(report._id, report.emailsBlocked)}
                        disabled={updating === report._id}
                        className={`p-2 rounded-lg transition-colors ${
                          report.emailsBlocked
                            ? 'text-expense-600 bg-expense-100 hover:bg-expense-200'
                            : 'text-surface-500 hover:text-surface-700 hover:bg-surface-100'
                        } disabled:opacity-50`}
                      >
                        {report.emailsBlocked ? <Ban className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setViewModal({ show: true, report })}
                        className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200">
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Reporter</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Owner</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report._id} className="border-b border-surface-100 hover:bg-surface-50">
                      <td className="py-4 px-4">
                        <div className="font-medium text-surface-900">{report.reporterEmail}</div>
                        {report.reporterName && (
                          <div className="text-sm text-surface-500">{report.reporterName}</div>
                        )}
                        <div className="text-xs text-surface-400">
                          {formatAdminDate(report.createdAt)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                          report.type === 'receivable' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {report.type?.charAt(0).toUpperCase() + report.type?.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-semibold text-surface-900">{formatAmount(report.reportedAmount)}</span>
                        <div className="text-xs text-surface-500 truncate max-w-[150px]">
                          {report.reportedDescription}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {report.reportedUser ? (
                          <>
                            <div className="font-medium text-surface-900">{report.reportedUser.name}</div>
                            <div className="text-sm text-surface-500">{report.reportedUser.email}</div>
                          </>
                        ) : (
                          <span className="text-surface-400 text-sm">Unknown</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <select
                          value={report.status}
                          onChange={(e) => handleStatusChange(report._id, e.target.value)}
                          disabled={updating === report._id}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(report.status)} disabled:opacity-50`}
                        >
                          <option value="pending">Pending</option>
                          <option value="investigating">Investigating</option>
                          <option value="resolved">Resolved</option>
                          <option value="dismissed">Dismissed</option>
                        </select>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleEmailBlock(report._id, report.emailsBlocked)}
                            disabled={updating === report._id}
                            className={`p-2 rounded-lg transition-colors ${
                              report.emailsBlocked
                                ? 'text-expense-600 bg-expense-100 hover:bg-expense-200'
                                : 'text-surface-500 hover:text-surface-700 hover:bg-surface-100'
                            } disabled:opacity-50`}
                            title={report.emailsBlocked ? 'Unblock Emails' : 'Block Emails'}
                          >
                            {report.emailsBlocked ? <Ban className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setViewModal({ show: true, report })}
                            className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
            <span className="text-sm text-surface-600">
              Page {pagination.page} of {pagination.pages} ({pagination.total} reports)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Report Modal */}
      {viewModal.show && viewModal.report && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <div>
                <h2 className="text-xl font-semibold text-surface-900">Report Details</h2>
                <p className="text-sm text-surface-500">ID: {viewModal.report._id}</p>
              </div>
              <button
                onClick={() => setViewModal({ show: false, report: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Reporter Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-surface-500">Reporter Email</label>
                  <p className="text-surface-900">{viewModal.report.reporterEmail}</p>
                </div>
                {viewModal.report.reporterName && (
                  <div>
                    <label className="text-sm font-medium text-surface-500">Reporter Name</label>
                    <p className="text-surface-900">{viewModal.report.reporterName}</p>
                  </div>
                )}
              </div>

              {/* Report Details */}
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h3 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                  <FileWarning className="w-4 h-4" />
                  Reported Entry
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-red-700">Type</label>
                    <p className="text-red-900 font-medium capitalize">{viewModal.report.type}</p>
                  </div>
                  <div>
                    <label className="text-sm text-red-700">Amount</label>
                    <p className="text-red-900 font-semibold">{formatAmount(viewModal.report.reportedAmount)}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm text-red-700">Description</label>
                    <p className="text-red-900">{viewModal.report.reportedDescription}</p>
                  </div>
                </div>
              </div>

              {/* Owner Info */}
              {viewModal.report.reportedUser && (
                <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
                  <h3 className="font-medium text-surface-900 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Entry Owner
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-surface-500">Name</label>
                      <p className="text-surface-900">{viewModal.report.reportedUser.name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-surface-500">Email</label>
                      <p className="text-surface-900">{viewModal.report.reportedUser.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reporter Message */}
              {viewModal.report.message && (
                <div>
                  <label className="text-sm font-medium text-surface-500">Reporter's Message</label>
                  <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-surface-900 whitespace-pre-wrap">{viewModal.report.message}</p>
                  </div>
                </div>
              )}

              {/* Status & Action */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-surface-500 mb-2 block">Status</label>
                  <select
                    value={viewModal.report.status}
                    onChange={(e) => {
                      handleStatusChange(viewModal.report._id, e.target.value);
                      setViewModal(prev => ({
                        ...prev,
                        report: { ...prev.report, status: e.target.value }
                      }));
                    }}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium border-0 ${getStatusColor(viewModal.report.status)}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="investigating">Investigating</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-surface-500 mb-2 block">Action Taken</label>
                  <select
                    value={viewModal.report.actionTaken}
                    onChange={(e) => {
                      handleActionChange(viewModal.report._id, e.target.value);
                      setViewModal(prev => ({
                        ...prev,
                        report: { ...prev.report, actionTaken: e.target.value }
                      }));
                    }}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium border-0 ${getActionColor(viewModal.report.actionTaken)}`}
                  >
                    <option value="none">None</option>
                    <option value="warning_sent">Warning Sent</option>
                    <option value="emails_blocked">Emails Blocked</option>
                    <option value="entry_deleted">Entry Deleted</option>
                    <option value="account_suspended">Account Suspended</option>
                  </select>
                </div>
              </div>

              {/* Email Block Toggle */}
              <div className="flex items-center justify-between p-4 bg-surface-50 rounded-xl border border-surface-200">
                <div>
                  <h4 className="font-medium text-surface-900">Block Emails to Reporter</h4>
                  <p className="text-sm text-surface-500">
                    {viewModal.report.emailsBlocked
                      ? 'Emails to this contact are currently blocked'
                      : 'Emails to this contact are allowed'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    handleToggleEmailBlock(viewModal.report._id, viewModal.report.emailsBlocked);
                    setViewModal(prev => ({
                      ...prev,
                      report: { ...prev.report, emailsBlocked: !prev.report.emailsBlocked }
                    }));
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewModal.report.emailsBlocked
                      ? 'bg-income-100 text-income-700 hover:bg-income-200'
                      : 'bg-expense-100 text-expense-700 hover:bg-expense-200'
                  }`}
                >
                  {viewModal.report.emailsBlocked ? 'Unblock Emails' : 'Block Emails'}
                </button>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-surface-500 mb-2 block">Admin Notes</label>
                <textarea
                  defaultValue={viewModal.report.adminNotes || ''}
                  onBlur={(e) => {
                    if (e.target.value !== viewModal.report.adminNotes) {
                      handleUpdateNotes(viewModal.report._id, e.target.value);
                    }
                  }}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600 resize-none"
                  placeholder="Add internal notes..."
                />
              </div>

              {/* Resolution Info */}
              {viewModal.report.resolvedAt && (
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Resolved</span>
                  </div>
                  <p className="text-sm text-green-600">
                    {new Date(viewModal.report.resolvedAt).toLocaleString('en-IN')}
                    {viewModal.report.resolvedBy && ` by ${viewModal.report.resolvedBy.name}`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Coupons Tab
function CouponsTab() {
  const { toast } = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    maxUses: '',
    maxUsesPerUser: 1,
    validFrom: '',
    validUntil: '',
    firstTimeOnly: false,
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, [pagination.page]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const { data } = await adminCouponAPI.getCoupons({ page: pagination.page, limit: 20 });
      setCoupons(data.data.coupons);
      setPagination(data.data.pagination);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      const { data } = await adminCouponAPI.generateCode();
      setFormData(prev => ({ ...prev, code: data.data.code }));
    } catch (err) {
      toast.error(err.error || 'Failed to generate code');
    }
  };

  const openCreateModal = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      maxUses: '',
      maxUsesPerUser: 1,
      validFrom: '',
      validUntil: '',
      firstTimeOnly: false,
      description: '',
      isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountType === 'flat' ? coupon.discountValue / 100 : coupon.discountValue,
      maxUses: coupon.maxUses || '',
      maxUsesPerUser: coupon.maxUsesPerUser || 1,
      validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split('T')[0] : '',
      validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().split('T')[0] : '',
      firstTimeOnly: coupon.firstTimeOnly || false,
      description: coupon.description || '',
      isActive: coupon.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }

    const discountVal = parseFloat(formData.discountValue);
    if (isNaN(discountVal) || discountVal <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    if (formData.discountType === 'percentage' && discountVal > 100) {
      toast.error('Percentage discount cannot exceed 100%');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        discountValue: formData.discountType === 'flat'
          ? Math.round(discountVal * 100)
          : discountVal,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        maxUsesPerUser: parseInt(formData.maxUsesPerUser),
        validFrom: formData.validFrom || null,
        validUntil: formData.validUntil || null
      };

      if (editingCoupon) {
        await adminCouponAPI.updateCoupon(editingCoupon._id, payload);
        toast.success('Coupon updated successfully');
      } else {
        await adminCouponAPI.createCoupon(payload);
        toast.success('Coupon created successfully');
      }
      setSaving(false);
      setShowModal(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.error || 'Failed to save coupon');
      setSaving(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await adminCouponAPI.deleteCoupon(couponId);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (err) {
      toast.error(err.error || 'Failed to delete coupon');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-surface-900">Coupons</h2>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center justify-center w-10 h-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2.5 rounded-xl font-medium"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline sm:ml-2">Create Coupon</span>
        </button>
      </div>

      {/* Coupons List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-surface-500">
          <Ticket className="w-12 h-12 mx-auto mb-4 text-surface-300" />
          <p>No coupons found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {coupons.map((coupon) => (
              <div key={coupon._id} className="border border-surface-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-surface-900">{coupon.code}</span>
                    <button
                      onClick={() => copyCode(coupon.code)}
                      className="p-1 text-surface-400 hover:text-surface-600 rounded"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-surface-100 text-surface-600'
                  }`}>
                    {coupon.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${
                    coupon.discountType === 'percentage' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {coupon.discountType === 'percentage' ? (
                      <><Percent className="w-3 h-3" />{coupon.discountValue}%</>
                    ) : (
                      <><IndianRupee className="w-3 h-3" />{(coupon.discountValue / 100).toFixed(0)}</>
                    )}
                  </span>
                  <span className="text-sm text-surface-500">
                    Used: {coupon.usedCount || 0}/{coupon.maxUses || '∞'}
                  </span>
                </div>

                {coupon.description && (
                  <p className="text-sm text-surface-500 mb-3 truncate">{coupon.description}</p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-surface-100">
                  <div className="text-xs text-surface-500">
                    {coupon.validUntil ? (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Expires: {formatAdminDate(coupon.validUntil)}
                      </span>
                    ) : (
                      'No expiry'
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(coupon)}
                      className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon._id)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Code</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Discount</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Usage</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Validity</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="border-b border-surface-100 hover:bg-surface-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-surface-900">{coupon.code}</span>
                        <button
                          onClick={() => copyCode(coupon.code)}
                          className="p-1 text-surface-400 hover:text-surface-600 rounded"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      {coupon.description && (
                        <p className="text-sm text-surface-500 truncate max-w-[200px]">{coupon.description}</p>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${
                        coupon.discountType === 'percentage' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {coupon.discountType === 'percentage' ? (
                          <><Percent className="w-3 h-3" />{coupon.discountValue}%</>
                        ) : (
                          <><IndianRupee className="w-3 h-3" />{(coupon.discountValue / 100).toFixed(0)}</>
                        )}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-surface-600">
                      {coupon.usedCount || 0} / {coupon.maxUses || '∞'}
                    </td>
                    <td className="py-4 px-4">
                      {coupon.validUntil ? (
                        <div className="flex items-center gap-1 text-sm text-surface-600">
                          <Calendar className="w-4 h-4" />
                          {formatAdminDate(coupon.validUntil)}
                        </div>
                      ) : (
                        <span className="text-surface-400">No expiry</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                        coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-surface-100 text-surface-600'
                      }`}>
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(coupon)}
                          className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
          <span className="text-sm text-surface-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">
                {editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Coupon Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    className="flex-1 px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="SAVE20"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleGenerateCode}
                    className="p-3 bg-surface-100 text-surface-700 rounded-xl hover:bg-surface-200"
                    title="Generate code"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Type</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Value</label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountValue: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={formData.discountType === 'percentage' ? '20' : '50'}
                    required
                    min="0"
                    max={formData.discountType === 'percentage' ? '100' : undefined}
                  />
                </div>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Max Total Uses</label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxUses: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Max Per User</label>
                  <input
                    type="number"
                    value={formData.maxUsesPerUser}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxUsesPerUser: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                    required
                  />
                </div>
              </div>

              {/* Validity Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Valid From</label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Valid Until</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Description (Internal)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Launch promo for new users"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.firstTimeOnly}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstTimeOnly: e.target.checked }))}
                    className="w-4 h-4 rounded border-surface-300"
                  />
                  <span className="text-sm text-surface-700">First-time subscribers only</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 rounded border-surface-300"
                  />
                  <span className="text-sm text-surface-700">Active</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Plans Tab
function PlansTab() {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingPlans, setCreatingPlans] = useState(false);
  const [formData, setFormData] = useState({
    freeAccessMode: false,
    iosPaymentHidden: false,
    trialDays: 30,
    billingInterval: 'monthly',
    monthlyPriceINR: 199,
    quarterlyPriceINR: 499,
    yearlyPriceINR: 1499,
    monthlyPriceUSD: 4.99,
    quarterlyPriceUSD: 12.99,
    yearlyPriceUSD: 49.99,
    iosMonthlyPriceINR: 299,
    iosQuarterlyPriceINR: 749,
    iosYearlyPriceINR: 2499,
    iosMonthlyPriceUSD: 6.99,
    iosQuarterlyPriceUSD: 17.99,
    iosYearlyPriceUSD: 59.99,
    planName: 'Premium',
    planDescription: 'Full access to all features',
    features: []
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await adminPlanAPI.getConfig();
      const cfg = data.data.config;
      setConfig(cfg);
      setFormData({
        freeAccessMode: cfg.freeAccessMode || false,
        iosPaymentHidden: cfg.iosPaymentHidden || false,
        trialDays: cfg.trialDays || 30,
        billingInterval: cfg.billingInterval || 'monthly',
        monthlyPriceINR: (cfg.monthlyPriceINR || 19900) / 100,
        quarterlyPriceINR: (cfg.quarterlyPriceINR || 49900) / 100,
        yearlyPriceINR: (cfg.yearlyPriceINR || 149900) / 100,
        monthlyPriceUSD: (cfg.monthlyPriceUSD || 499) / 100,
        quarterlyPriceUSD: (cfg.quarterlyPriceUSD || 1299) / 100,
        yearlyPriceUSD: (cfg.yearlyPriceUSD || 4999) / 100,
        iosMonthlyPriceINR: (cfg.iosMonthlyPriceINR || 29900) / 100,
        iosQuarterlyPriceINR: (cfg.iosQuarterlyPriceINR || 74900) / 100,
        iosYearlyPriceINR: (cfg.iosYearlyPriceINR || 249900) / 100,
        iosMonthlyPriceUSD: (cfg.iosMonthlyPriceUSD || 699) / 100,
        iosQuarterlyPriceUSD: (cfg.iosQuarterlyPriceUSD || 1799) / 100,
        iosYearlyPriceUSD: (cfg.iosYearlyPriceUSD || 5999) / 100,
        planName: cfg.planName || 'Premium',
        planDescription: cfg.planDescription || 'Full access to all features',
        features: cfg.features || []
      });
    } catch (err) {
      toast.error(err.error || 'Failed to fetch plan config');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminPlanAPI.updateConfig({
        ...formData,
        monthlyPriceINR: Math.round(formData.monthlyPriceINR * 100),
        quarterlyPriceINR: Math.round(formData.quarterlyPriceINR * 100),
        yearlyPriceINR: Math.round(formData.yearlyPriceINR * 100),
        monthlyPriceUSD: Math.round(formData.monthlyPriceUSD * 100),
        quarterlyPriceUSD: Math.round(formData.quarterlyPriceUSD * 100),
        yearlyPriceUSD: Math.round(formData.yearlyPriceUSD * 100),
        iosMonthlyPriceINR: Math.round(formData.iosMonthlyPriceINR * 100),
        iosQuarterlyPriceINR: Math.round(formData.iosQuarterlyPriceINR * 100),
        iosYearlyPriceINR: Math.round(formData.iosYearlyPriceINR * 100),
        iosMonthlyPriceUSD: Math.round(formData.iosMonthlyPriceUSD * 100),
        iosQuarterlyPriceUSD: Math.round(formData.iosQuarterlyPriceUSD * 100),
        iosYearlyPriceUSD: Math.round(formData.iosYearlyPriceUSD * 100)
      });
      toast.success('Plan configuration updated');
      fetchConfig();
    } catch (err) {
      toast.error(err.error || 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleCreateRazorpayPlans = async () => {
    setCreatingPlans(true);
    try {
      const { data } = await adminPlanAPI.createRazorpayPlans();
      const { created, skipped, errors } = data.data;

      if (created.length > 0) {
        toast.success(`Created ${created.length} Razorpay plan(s)`);
      }
      if (skipped.length > 0) {
        toast.info(`${skipped.length} plan(s) already existed`);
      }
      if (errors.length > 0) {
        toast.error(`Failed to create ${errors.length} plan(s): ${errors.map(e => `${e.interval} ${e.currency}`).join(', ')}`);
      }

      // Refresh config to show new plan IDs
      fetchConfig();
    } catch (err) {
      toast.error(err.error || 'Failed to create Razorpay plans');
    } finally {
      setCreatingPlans(false);
    }
  };

  // Check if Razorpay plans are configured (including iOS if iOS prices are set)
  const hasAllPlans = config &&
    config.razorpayPlanIdMonthlyINR &&
    config.razorpayPlanIdQuarterlyINR &&
    config.razorpayPlanIdYearlyINR &&
    (!config.monthlyPriceUSD || config.razorpayPlanIdMonthlyUSD) &&
    (!config.quarterlyPriceUSD || config.razorpayPlanIdQuarterlyUSD) &&
    (!config.yearlyPriceUSD || config.razorpayPlanIdYearlyUSD) &&
    (!config.iosMonthlyPriceINR || config.iosRazorpayPlanIdMonthlyINR) &&
    (!config.iosQuarterlyPriceINR || config.iosRazorpayPlanIdQuarterlyINR) &&
    (!config.iosYearlyPriceINR || config.iosRazorpayPlanIdYearlyINR) &&
    (!config.iosMonthlyPriceUSD || config.iosRazorpayPlanIdMonthlyUSD) &&
    (!config.iosQuarterlyPriceUSD || config.iosRazorpayPlanIdQuarterlyUSD) &&
    (!config.iosYearlyPriceUSD || config.iosRazorpayPlanIdYearlyUSD);

  const missingPlans = config ? [
    !config.razorpayPlanIdMonthlyINR && 'Monthly INR',
    !config.razorpayPlanIdQuarterlyINR && 'Quarterly INR',
    !config.razorpayPlanIdYearlyINR && 'Yearly INR',
    config.monthlyPriceUSD && !config.razorpayPlanIdMonthlyUSD && 'Monthly USD',
    config.quarterlyPriceUSD && !config.razorpayPlanIdQuarterlyUSD && 'Quarterly USD',
    config.yearlyPriceUSD && !config.razorpayPlanIdYearlyUSD && 'Yearly USD',
    config.iosMonthlyPriceINR && !config.iosRazorpayPlanIdMonthlyINR && 'iOS Monthly INR',
    config.iosQuarterlyPriceINR && !config.iosRazorpayPlanIdQuarterlyINR && 'iOS Quarterly INR',
    config.iosYearlyPriceINR && !config.iosRazorpayPlanIdYearlyINR && 'iOS Yearly INR',
    config.iosMonthlyPriceUSD && !config.iosRazorpayPlanIdMonthlyUSD && 'iOS Monthly USD',
    config.iosQuarterlyPriceUSD && !config.iosRazorpayPlanIdQuarterlyUSD && 'iOS Quarterly USD',
    config.iosYearlyPriceUSD && !config.iosRazorpayPlanIdYearlyUSD && 'iOS Yearly USD',
  ].filter(Boolean) : [];

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      <h2 className="text-xl font-semibold text-surface-900 mb-6">Plan Configuration</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Free Access Mode — hidden, not needed currently */}
        {/* iOS Payment Hidden — hidden, not needed currently */}

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Plan Name</label>
            <input
              type="text"
              value={formData.planName}
              onChange={(e) => setFormData(prev => ({ ...prev, planName: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Plan Description</label>
            <input
              type="text"
              value={formData.planDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, planDescription: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Trial & Billing Interval */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Trial Days</label>
            <div className="relative">
              <input
                type="number"
                value={formData.trialDays}
                onChange={(e) => setFormData(prev => ({ ...prev, trialDays: parseInt(e.target.value) }))}
                className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                min="0"
                max="365"
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400">days</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-2">Default Billing Interval</label>
            <select
              value={formData.billingInterval}
              onChange={(e) => setFormData(prev => ({ ...prev, billingInterval: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
            <p className="text-xs text-surface-500 mt-1">This is shown on the marketing site</p>
          </div>
        </div>

        {/* Pricing - All Intervals */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-3">Pricing (INR)</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-surface-50 rounded-xl">
              <label className="block text-xs font-medium text-surface-500 mb-2">Monthly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.monthlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">/month</p>
            </div>
            <div className="p-4 bg-surface-50 rounded-xl">
              <label className="block text-xs font-medium text-surface-500 mb-2">Quarterly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.quarterlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, quarterlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">/3 months ({formData.quarterlyPriceINR > 0 ? `₹${Math.round(formData.quarterlyPriceINR / 3)}/mo` : ''})</p>
            </div>
            <div className="p-4 bg-surface-50 rounded-xl">
              <label className="block text-xs font-medium text-surface-500 mb-2">Yearly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.yearlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-surface-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">/year ({formData.yearlyPriceINR > 0 ? `₹${Math.round(formData.yearlyPriceINR / 12)}/mo` : ''})</p>
            </div>
          </div>
        </div>

        {/* USD Pricing */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1">International Pricing (USD)</label>
          <p className="text-xs text-surface-500 mb-3">For users outside India (detected by phone number or timezone)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Monthly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.monthlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/month</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Quarterly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.quarterlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, quarterlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/3 months ({formData.quarterlyPriceUSD > 0 ? `$${(formData.quarterlyPriceUSD / 3).toFixed(2)}/mo` : ''})</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Yearly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.yearlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/year ({formData.yearlyPriceUSD > 0 ? `$${(formData.yearlyPriceUSD / 12).toFixed(2)}/mo` : ''})</p>
            </div>
          </div>
        </div>

        {/* iOS Pricing */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1">iOS Pricing (INR)</label>
          <p className="text-xs text-surface-500 mb-3 flex items-center gap-1">
            <Smartphone className="w-3 h-3" />
            Higher pricing for users subscribing via iOS email link
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <label className="block text-xs font-medium text-amber-700 mb-2">Monthly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosMonthlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosMonthlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-amber-600 mt-1">/month</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <label className="block text-xs font-medium text-amber-700 mb-2">Quarterly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosQuarterlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosQuarterlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-amber-600 mt-1">/3 months ({formData.iosQuarterlyPriceINR > 0 ? `₹${Math.round(formData.iosQuarterlyPriceINR / 3)}/mo` : ''})</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <label className="block text-xs font-medium text-amber-700 mb-2">Yearly</label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosYearlyPriceINR}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosYearlyPriceINR: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-amber-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  min="0"
                  step="1"
                  required
                />
              </div>
              <p className="text-xs text-amber-600 mt-1">/year ({formData.iosYearlyPriceINR > 0 ? `₹${Math.round(formData.iosYearlyPriceINR / 12)}/mo` : ''})</p>
            </div>
          </div>
        </div>

        {/* iOS Pricing USD */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1">iOS Pricing (USD)</label>
          <p className="text-xs text-surface-500 mb-3 flex items-center gap-1">
            <Smartphone className="w-3 h-3" />
            Higher USD pricing for international iOS users subscribing via web
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Monthly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosMonthlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosMonthlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/month</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Quarterly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosQuarterlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosQuarterlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/3 months ({formData.iosQuarterlyPriceUSD > 0 ? `$${(formData.iosQuarterlyPriceUSD / 3).toFixed(2)}/mo` : ''})</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="block text-xs font-medium text-blue-700 mb-2">Yearly</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                <input
                  type="number"
                  value={formData.iosYearlyPriceUSD}
                  onChange={(e) => setFormData(prev => ({ ...prev, iosYearlyPriceUSD: parseFloat(e.target.value) }))}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-blue-200 rounded-lg text-surface-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <p className="text-xs text-blue-600 mt-1">/year ({formData.iosYearlyPriceUSD > 0 ? `$${(formData.iosYearlyPriceUSD / 12).toFixed(2)}/mo` : ''})</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-2">Plan Features</label>
          <div className="space-y-2 mb-3">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-surface-50 rounded-xl">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="flex-1 text-surface-700">{feature}</span>
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="p-1 text-surface-400 hover:text-red-500 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
              className="flex-1 px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Add a feature..."
            />
            <button
              type="button"
              onClick={addFeature}
              className="px-4 py-3 bg-surface-100 text-surface-700 rounded-xl hover:bg-surface-200"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end pt-4 border-t border-surface-200">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
            Save Configuration
          </button>
        </div>
      </form>

      {/* Razorpay Plans Section */}
      <div className="mt-6 p-6 bg-surface-50 rounded-xl border border-surface-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-surface-900">Razorpay Plans</h3>
            <p className="text-sm text-surface-500 mt-1">
              {hasAllPlans
                ? 'All billing plans are configured in Razorpay'
                : `Missing plans: ${missingPlans.join(', ')}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasAllPlans && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs font-medium">All Configured</span>
              </div>
            )}
            <button
              onClick={handleCreateRazorpayPlans}
              disabled={creatingPlans}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50"
            >
              {creatingPlans ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Sync Razorpay Plans
                </>
              )}
            </button>
          </div>
        </div>

        {/* Show existing plan IDs */}
        {config && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdMonthlyINR ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Monthly INR</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdMonthlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.razorpayPlanIdMonthlyINR || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdQuarterlyINR ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Quarterly INR</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdQuarterlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.razorpayPlanIdQuarterlyINR || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdYearlyINR ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Yearly INR</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdYearlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.razorpayPlanIdYearlyINR || 'Not configured'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* USD Razorpay Plans Section */}
      <div className="mt-6 p-6 bg-blue-50/50 rounded-xl border border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold text-surface-900">USD Razorpay Plans</h3>
            <p className="text-sm text-surface-500 mt-1">
              International plans for users outside India. Created automatically when USD prices are set.
            </p>
          </div>
        </div>

        {config && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdMonthlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Monthly USD</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdMonthlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.razorpayPlanIdMonthlyUSD || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdQuarterlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Quarterly USD</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdQuarterlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.razorpayPlanIdQuarterlyUSD || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.razorpayPlanIdYearlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">Yearly USD</p>
              <p className={`text-sm font-mono truncate ${config.razorpayPlanIdYearlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.razorpayPlanIdYearlyUSD || 'Not configured'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* iOS Razorpay Plans Section */}
      <div className="mt-6 p-6 bg-amber-50/50 rounded-xl border border-amber-200">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="font-semibold text-surface-900">iOS Razorpay Plans</h3>
            <p className="text-sm text-surface-500 mt-1">
              Separate Razorpay plans with higher pricing for iOS users. Created automatically via the button above when iOS prices are set.
            </p>
          </div>
        </div>

        {config && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdMonthlyINR ? 'bg-green-50 border border-green-200' : 'bg-white border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Monthly INR</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdMonthlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.iosRazorpayPlanIdMonthlyINR || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdQuarterlyINR ? 'bg-green-50 border border-green-200' : 'bg-white border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Quarterly INR</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdQuarterlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.iosRazorpayPlanIdQuarterlyINR || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdYearlyINR ? 'bg-green-50 border border-green-200' : 'bg-white border border-amber-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Yearly INR</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdYearlyINR ? 'text-green-700' : 'text-amber-700'}`}>
                {config.iosRazorpayPlanIdYearlyINR || 'Not configured'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* iOS USD Razorpay Plans Section */}
      <div className="mt-6 p-6 bg-blue-50/30 rounded-xl border border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold text-surface-900">iOS USD Razorpay Plans</h3>
            <p className="text-sm text-surface-500 mt-1">
              Higher USD pricing for international iOS users. Created automatically when iOS USD prices are set.
            </p>
          </div>
        </div>

        {config && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdMonthlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Monthly USD</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdMonthlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.iosRazorpayPlanIdMonthlyUSD || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdQuarterlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Quarterly USD</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdQuarterlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.iosRazorpayPlanIdQuarterlyUSD || 'Not configured'}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${config.iosRazorpayPlanIdYearlyUSD ? 'bg-green-50 border border-green-200' : 'bg-white border border-blue-200'}`}>
              <p className="text-xs font-medium text-surface-500 mb-1">iOS Yearly USD</p>
              <p className={`text-sm font-mono truncate ${config.iosRazorpayPlanIdYearlyUSD ? 'text-green-700' : 'text-blue-700'}`}>
                {config.iosRazorpayPlanIdYearlyUSD || 'Not configured'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Subscriptions Tab
function SubscriptionsTab() {
  const { toast } = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [actionModal, setActionModal] = useState({ show: false, subscription: null, type: null });
  const [actionData, setActionData] = useState({ days: 30, reason: '' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
  }, [pagination.page, statusFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;

      const { data } = await adminSubscriptionsAPI.getSubscriptions(params);
      setSubscriptions(data.data.subscriptions);
      setPagination(data.data.pagination);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await adminSubscriptionsAPI.getStats();
      setStats(data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSubscriptions();
  };

  const handleSendPaymentLink = async (userId) => {
    try {
      const { data } = await adminSubscriptionsAPI.sendPaymentLink(userId);
      toast.success(data.message || 'Payment link sent!');
    } catch (err) {
      toast.error(err.error || 'Failed to send payment link');
    }
  };

  const handleViewInvoice = async (userId) => {
    try {
      const { data } = await adminSubscriptionsAPI.getInvoices(userId);
      const invoices = data.data?.invoices || [];
      if (invoices.length === 0 || !invoices[0].url) {
        toast.error('No invoice found for this user');
        return;
      }
      window.open(invoices[0].url, '_blank');
    } catch (err) {
      toast.error('Failed to fetch invoice');
    }
  };

  const openExtendModal = (sub) => {
    setActionModal({ show: true, subscription: sub, type: 'extend' });
    setActionData({ days: 30, reason: '' });
  };

  const openCancelModal = (sub) => {
    setActionModal({ show: true, subscription: sub, type: 'cancel' });
    setActionData({ days: 0, reason: '', immediate: false });
  };

  const openReactivateModal = (sub) => {
    setActionModal({ show: true, subscription: sub, type: 'reactivate' });
    setActionData({ days: 30, reason: '' });
  };

  const handleAction = async () => {
    setActionLoading(true);
    try {
      if (actionModal.type === 'extend') {
        await adminSubscriptionsAPI.extendSubscription(
          actionModal.subscription.user._id,
          actionData.days,
          actionData.reason
        );
        toast.success('Subscription extended successfully');
      } else if (actionModal.type === 'reactivate') {
        await adminSubscriptionsAPI.reactivateSubscription(
          actionModal.subscription.user._id,
          actionData.days,
          actionData.reason
        );
        toast.success(`Subscription reactivated for ${actionData.days} days`);
      } else {
        await adminSubscriptionsAPI.cancelSubscription(
          actionModal.subscription.user._id,
          actionData.immediate,
          actionData.reason
        );
        toast.success('Subscription cancelled');
      }
      setActionModal({ show: false, subscription: null, type: null });
      fetchSubscriptions();
      fetchStats();
    } catch (err) {
      toast.error(err.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'trial': return 'bg-amber-100 text-amber-700';
      case 'active': return 'bg-green-100 text-green-700';
      case 'paused': return 'bg-blue-100 text-blue-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'expired': return 'bg-surface-100 text-surface-600';
      default: return 'bg-surface-100 text-surface-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="space-y-3">
          {/* Status counts */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <div className="bg-amber-50 rounded-xl p-2.5 sm:p-3 border border-amber-200 cursor-pointer hover:ring-2 hover:ring-amber-300" onClick={() => setStatusFilter('trial')}>
              <span className="text-[10px] sm:text-xs font-medium text-amber-600">Trial</span>
              <p className="text-lg sm:text-xl font-bold text-amber-800">{stats.subscriptions?.trial || 0}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-2.5 sm:p-3 border border-green-200 cursor-pointer hover:ring-2 hover:ring-green-300" onClick={() => setStatusFilter('active')}>
              <span className="text-[10px] sm:text-xs font-medium text-green-600">Active</span>
              <p className="text-lg sm:text-xl font-bold text-green-800">{stats.subscriptions?.active || 0}</p>
              <p className="text-[9px] sm:text-[10px] text-green-600">{stats.subscriptions?.activePaid || 0} paid · {stats.subscriptions?.activeExtended || 0} ext</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-2.5 sm:p-3 border border-orange-200 cursor-pointer hover:ring-2 hover:ring-orange-300" onClick={() => setStatusFilter('cancelling')}>
              <span className="text-[10px] sm:text-xs font-medium text-orange-600">Cancelling</span>
              <p className="text-lg sm:text-xl font-bold text-orange-800">{stats.subscriptions?.cancelling || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-2.5 sm:p-3 border border-blue-200 cursor-pointer hover:ring-2 hover:ring-blue-300" onClick={() => setStatusFilter('paused')}>
              <span className="text-[10px] sm:text-xs font-medium text-blue-600">Paused</span>
              <p className="text-lg sm:text-xl font-bold text-blue-800">{stats.subscriptions?.paused || 0}</p>
            </div>
            <div className="bg-surface-100 rounded-xl p-2.5 sm:p-3 border border-surface-200 cursor-pointer hover:ring-2 hover:ring-surface-300" onClick={() => setStatusFilter('cancelled')}>
              <span className="text-[10px] sm:text-xs font-medium text-surface-500">Cancelled</span>
              <p className="text-lg sm:text-xl font-bold text-surface-700">{stats.subscriptions?.cancelled || 0}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-2.5 sm:p-3 border border-red-200 cursor-pointer hover:ring-2 hover:ring-red-300" onClick={() => setStatusFilter('expired')}>
              <span className="text-[10px] sm:text-xs font-medium text-red-600">Expired</span>
              <p className="text-lg sm:text-xl font-bold text-red-800">{stats.subscriptions?.expired || 0}</p>
            </div>
          </div>

          {/* Revenue */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="bg-green-50 rounded-xl p-2.5 sm:p-3 border border-green-200">
              <span className="text-[10px] sm:text-xs font-medium text-green-600">Total Revenue</span>
              <p className="text-lg sm:text-xl font-bold text-green-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                ₹{((stats.revenue?.total || 0) / 100).toLocaleString('en-IN')}
              </p>
              <p className="text-[9px] sm:text-[10px] text-green-600">{stats.revenue?.totalPayments || 0} payments</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-2.5 sm:p-3 border border-emerald-200">
              <span className="text-[10px] sm:text-xs font-medium text-emerald-600">This Month</span>
              <p className="text-lg sm:text-xl font-bold text-emerald-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                ₹{((stats.revenue?.thisMonth || 0) / 100).toLocaleString('en-IN')}
              </p>
              <p className="text-[9px] sm:text-[10px] text-emerald-600">{stats.revenue?.thisMonthPayments || 0} payments</p>
            </div>
            <div className="bg-surface-50 rounded-xl p-2.5 sm:p-3 border border-surface-200 col-span-2 sm:col-span-1">
              <span className="text-[10px] sm:text-xs font-medium text-surface-500">Total Users</span>
              <p className="text-lg sm:text-xl font-bold text-surface-800">{stats.subscriptions?.total || 0}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-surface-200 p-3 sm:p-6">
        <h2 className="text-[15px] sm:text-xl font-semibold text-surface-900 mb-4 sm:mb-6">User Subscriptions</h2>

        {/* Filters */}
        <div className="flex gap-2 sm:gap-4 mb-4 sm:mb-6">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </form>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All</option>
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="cancelling">Cancelling</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        {/* Subscriptions List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-12 text-surface-500">
            <Crown className="w-12 h-12 mx-auto mb-4 text-surface-300" />
            <p>No subscriptions found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="xl:hidden space-y-3">
              {subscriptions.map((sub) => (
                <div key={sub._id} className="border border-surface-200 rounded-xl p-3 space-y-2.5">
                  {/* User & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-surface-900 truncate">{sub.user?.name}</div>
                      <div className="text-[11px] text-surface-400 truncate">{sub.user?.email}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${getStatusColor(sub.status)}`}>
                        {sub.status}
                      </span>
                      {sub.status === 'active' && (
                        <>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${sub.isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {sub.isPaid ? 'Paid' : 'Ext'}
                          </span>
                          {sub.isCancelling && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-100 text-red-700">
                              Cancelling
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Plan & Period & Payment */}
                  {sub.planLabel && (
                    <div className="text-[10px] text-surface-500">
                      <span className="bg-surface-100 px-1.5 py-0.5 rounded font-medium">{sub.planLabel}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-surface-400">
                      Ends: {sub.status === 'trial' && sub.trialEndDate
                        ? formatAdminDate(sub.trialEndDate)
                        : sub.currentPeriodEnd
                          ? formatAdminDate(sub.currentPeriodEnd)
                          : '-'}
                    </span>
                    {sub.paymentInfo ? (
                      <div className="text-right">
                        <span className="font-semibold text-surface-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {sub.paymentInfo.currency === 'USD' ? '$' : '₹'}{(sub.paymentInfo.total / 100).toFixed(sub.paymentInfo.currency === 'USD' ? 2 : 0)}
                          {sub.paymentInfo.count > 1 && <span className="text-[9px] text-surface-400 ml-0.5">×{sub.paymentInfo.count}</span>}
                        </span>
                      </div>
                    ) : (
                      <span className="text-surface-400">-</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-surface-100">
                    {sub.status !== 'cancelled' && (
                      <button
                        onClick={() => openExtendModal(sub)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-green-50 text-green-700 active:bg-green-100"
                      >
                        <Play className="w-3 h-3" /> Extend
                      </button>
                    )}
                    {sub.status === 'cancelled' && (
                      <button
                        onClick={() => openReactivateModal(sub)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 active:bg-amber-100"
                      >
                        <Play className="w-3 h-3" /> Reactivate
                      </button>
                    )}
                    <button
                      onClick={() => handleSendPaymentLink(sub.user?._id || sub.user)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-blue-50 text-blue-700 active:bg-blue-100"
                    >
                      <Send className="w-3 h-3" /> Payment Link
                    </button>
                    {sub.isPaid && (
                      <button
                        onClick={() => handleViewInvoice(sub.user?._id || sub.user)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-purple-50 text-purple-700 active:bg-purple-100"
                      >
                        <FileText className="w-3 h-3" /> Invoice
                      </button>
                    )}
                    {['trial', 'active', 'paused'].includes(sub.status) && (
                      <button
                        onClick={() => openCancelModal(sub)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-red-50 text-red-700 active:bg-red-100 ml-auto"
                      >
                        <X className="w-3 h-3" /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden xl:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200">
                    <th className="text-left py-3 px-4 font-medium text-surface-600">User</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Period End</th>
                    <th className="text-left py-3 px-4 font-medium text-surface-600">Payment</th>
                    <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub._id} className="border-b border-surface-100 hover:bg-surface-50">
                      <td className="py-4 px-4">
                        <div className="font-medium text-surface-900">{sub.user?.name}</div>
                        <div className="text-sm text-surface-500">{sub.user?.email}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(sub.status)}`}>
                            {sub.status}
                          </span>
                          {sub.status === 'active' && (
                            <>
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${sub.isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {sub.isPaid ? 'Paid' : 'Extended'}
                              </span>
                              {sub.isCancelling && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                                  Cancelling
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-surface-600">
                        {sub.status === 'trial' && sub.trialEndDate
                          ? formatAdminDate(sub.trialEndDate)
                          : sub.currentPeriodEnd
                            ? formatAdminDate(sub.currentPeriodEnd)
                            : '-'}
                      </td>
                      <td className="py-4 px-4">
                        {sub.paymentInfo ? (
                          <div>
                            <div className="font-medium text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {sub.paymentInfo.currency === 'USD' ? '$' : '₹'}{(sub.paymentInfo.total / 100).toFixed(sub.paymentInfo.currency === 'USD' ? 2 : 0)}
                              {sub.paymentInfo.count > 1 && (
                                <span className="text-[10px] text-surface-400 ml-1">×{sub.paymentInfo.count}</span>
                              )}
                            </div>
                            {sub.planLabel && <div className="text-[10px] text-surface-500">{sub.planLabel}</div>}
                            <div className="text-[10px] text-surface-400 font-mono">{sub.paymentInfo.lastPaymentId}</div>
                          </div>
                        ) : (
                          <span className="text-surface-400">No payment</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {sub.status !== 'cancelled' && (
                            <button
                              onClick={() => openExtendModal(sub)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Extend"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {sub.status === 'cancelled' && (
                            <button
                              onClick={() => openReactivateModal(sub)}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                              title="Reactivate"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleSendPaymentLink(sub.user?._id || sub.user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Send Payment Link"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          {sub.isPaid && (
                            <button
                              onClick={() => handleViewInvoice(sub.user?._id || sub.user)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="View Invoice"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                          {['trial', 'active', 'paused'].includes(sub.status) && (
                            <button
                              onClick={() => openCancelModal(sub)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
            <span className="text-sm text-surface-600">
              Page {pagination.page} of {pagination.pages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {actionModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-surface-200">
              <h2 className="text-xl font-semibold text-surface-900">
                {actionModal.type === 'extend' ? 'Extend Subscription' : actionModal.type === 'reactivate' ? 'Reactivate Subscription' : 'Cancel Subscription'}
              </h2>
              <button
                onClick={() => setActionModal({ show: false, subscription: null, type: null })}
                className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-surface-50 rounded-xl">
                <p className="text-sm text-surface-600">
                  User: <span className="font-medium text-surface-900">{actionModal.subscription?.user?.name}</span>
                </p>
                <p className="text-sm text-surface-500">{actionModal.subscription?.user?.email}</p>
              </div>

              {(actionModal.type === 'extend' || actionModal.type === 'reactivate') && (
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-2">
                    {actionModal.type === 'reactivate' ? 'Reactivate for (days)' : 'Extend by (days)'}
                  </label>
                  <input
                    type="number"
                    value={actionData.days}
                    onChange={(e) => setActionData(prev => ({ ...prev, days: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                    max="365"
                  />
                </div>
              )}

              {actionModal.type === 'cancel' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={actionData.immediate}
                    onChange={(e) => setActionData(prev => ({ ...prev, immediate: e.target.checked }))}
                    className="w-4 h-4 rounded border-surface-300"
                  />
                  <span className="text-sm text-surface-700">Cancel immediately (end access now)</span>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Reason (optional)</label>
                <textarea
                  value={actionData.reason}
                  onChange={(e) => setActionData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Internal note..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-200">
              <button
                onClick={() => setActionModal({ show: false, subscription: null, type: null })}
                className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={actionLoading}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium disabled:opacity-50 ${
                  actionModal.type === 'cancel'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : actionModal.type === 'reactivate'
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                {actionModal.type === 'extend' ? 'Extend' : actionModal.type === 'reactivate' ? 'Reactivate' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Received Emails Tab
function ReceivedEmailsTab() {
  const { toast } = useToast();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [viewModal, setViewModal] = useState({ show: false, email: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, email: null });
  const [replyModal, setReplyModal] = useState({ show: false, email: null });
  const [replyData, setReplyData] = useState({ subject: '', message: '' });
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchEmails();
  }, [pagination.page, statusFilter]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;

      const { data } = await adminAPI.getReceivedEmails(params);
      setEmails(data.data.emails);
      setPagination(data.data.pagination);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch emails');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchEmails();
  };

  const handleStatusChange = async (emailId, status) => {
    try {
      await adminAPI.updateReceivedEmail(emailId, { status });
      toast.success('Email status updated');
      fetchEmails();
    } catch (err) {
      toast.error(err.error || 'Failed to update email');
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.email) return;

    try {
      setDeleting(true);
      await adminAPI.deleteReceivedEmail(deleteModal.email._id);
      toast.success('Email deleted successfully');
      setDeleteModal({ show: false, email: null });
      fetchEmails();
    } catch (err) {
      toast.error(err.error || 'Failed to delete email');
    } finally {
      setDeleting(false);
    }
  };

  const openReplyModal = (email) => {
    setReplyModal({ show: true, email });
    setReplyData({
      subject: `Re: ${email.subject || '(No subject)'}`,
      message: ''
    });
  };

  const handleSendReply = async () => {
    if (!replyData.subject || !replyData.message) {
      toast.error('Subject and message are required');
      return;
    }

    try {
      setSending(true);
      await adminAPI.replyToReceivedEmail(replyModal.email._id, replyData.subject, replyData.message);
      toast.success('Reply sent successfully');
      setReplyModal({ show: false, email: null });
      setReplyData({ subject: '', message: '' });
      fetchEmails();
    } catch (err) {
      toast.error(err.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-amber-100 text-amber-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'processed': return 'bg-income-100 text-income-700';
      case 'failed': return 'bg-expense-100 text-expense-700';
      default: return 'bg-surface-100 text-surface-700';
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      <h2 className="text-xl font-semibold text-surface-900 mb-6">Received Emails</h2>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by from, to, or subject..."
            className="w-full pl-12 pr-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Status</option>
          <option value="received">Received</option>
          <option value="processing">Processing</option>
          <option value="processed">Processed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Emails Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12">
          <Inbox className="w-12 h-12 text-surface-300 mx-auto mb-3" />
          <p className="text-surface-500">No emails found</p>
          <p className="text-sm text-surface-400 mt-1">Emails sent to your Resend receiving address will appear here</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="xl:hidden space-y-4">
            {emails.map((email) => (
              <div key={email._id} className="border border-surface-200 rounded-xl p-4 space-y-3">
                {/* Subject & Attachments */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-surface-900 truncate">{email.subject || '(No subject)'}</p>
                    {email.attachments?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-surface-500 mt-1">
                        <Paperclip className="w-3 h-3" />
                        {email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <select
                    value={email.status}
                    onChange={(e) => handleStatusChange(email._id, e.target.value)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer flex-shrink-0 ${getStatusColor(email.status)}`}
                  >
                    <option value="received">Received</option>
                    <option value="processing">Processing</option>
                    <option value="processed">Processed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                {/* From/To */}
                <div className="space-y-1 text-sm">
                  <p className="text-surface-600">
                    <span className="text-surface-400">From:</span>{' '}
                    <span className="break-all">{email.from}</span>
                  </p>
                  <p className="text-surface-600">
                    <span className="text-surface-400">To:</span>{' '}
                    <span className="break-all">{email.to?.join(', ')}</span>
                  </p>
                </div>

                {/* Linked User & Date */}
                <div className="flex items-center justify-between text-xs text-surface-500">
                  {email.user ? (
                    <span>Linked to: {email.user.name || email.user.email}</span>
                  ) : (
                    <span></span>
                  )}
                  <span>
                    {new Date(email.receivedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-surface-100">
                  <button
                    onClick={() => setViewModal({ show: true, email })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-surface-100 text-surface-700 hover:bg-surface-200"
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => openReplyModal(email)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <Send className="w-4 h-4" /> Reply
                  </button>
                  <button
                    onClick={() => setDeleteModal({ show: true, email })}
                    className="p-2 text-expense-500 hover:text-expense-700 hover:bg-expense-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 font-medium text-surface-600">From</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Subject</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-surface-600">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-surface-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email._id} className="border-b border-surface-100 hover:bg-surface-50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-surface-900 truncate max-w-[200px]">{email.from}</div>
                      <div className="text-sm text-surface-500 truncate max-w-[200px]">
                        To: {email.to?.join(', ')}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-surface-900 truncate max-w-[250px]">{email.subject || '(No subject)'}</span>
                        {email.attachments?.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-surface-500">
                            <Paperclip className="w-3 h-3" />
                            {email.attachments.length}
                          </span>
                        )}
                      </div>
                      {email.user && (
                        <div className="text-sm text-surface-500">
                          Linked to: {email.user.name || email.user.email}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4 text-surface-600 whitespace-nowrap">
                      {new Date(email.receivedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={email.status}
                        onChange={(e) => handleStatusChange(email._id, e.target.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(email.status)}`}
                      >
                        <option value="received">Received</option>
                        <option value="processing">Processing</option>
                        <option value="processed">Processed</option>
                        <option value="failed">Failed</option>
                      </select>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setViewModal({ show: true, email })}
                          className="p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReplyModal(email)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                          title="Reply"
                        >
                          <Send className="w-4 h-4" /> Reply
                        </button>
                        <button
                          onClick={() => setDeleteModal({ show: true, email })}
                          className="p-2 text-expense-500 hover:text-expense-700 hover:bg-expense-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-surface-200">
          <span className="text-sm text-surface-600">
            Page {pagination.page} of {pagination.pages} ({pagination.total} emails)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-surface-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-surface-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Email Modal */}
      {viewModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="sticky top-0 bg-white flex items-center justify-between p-4 sm:p-6 border-b border-surface-200 z-10">
              <h2 className="text-lg sm:text-xl font-semibold text-surface-900">Email Details</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setViewModal({ show: false, email: null });
                    openReplyModal(viewModal.email);
                  }}
                  className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                >
                  <Send className="w-4 h-4" /> <span className="hidden sm:inline">Reply</span>
                </button>
                <button
                  onClick={() => setViewModal({ show: false, email: null })}
                  className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {/* Email Header Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">From</label>
                  <p className="text-surface-900 mt-1 break-all text-sm sm:text-base">{viewModal.email?.from}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">To</label>
                  <p className="text-surface-900 mt-1 break-all text-sm sm:text-base">{viewModal.email?.to?.join(', ')}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">Subject</label>
                  <p className="text-surface-900 mt-1 font-medium text-sm sm:text-base">{viewModal.email?.subject || '(No subject)'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">Received</label>
                  <p className="text-surface-900 mt-1 text-sm sm:text-base">
                    {new Date(viewModal.email?.receivedAt).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Email Body */}
              <div className="pt-4 border-t border-surface-200">
                <label className="text-xs font-medium text-surface-500 uppercase tracking-wide flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4" /> Message
                </label>
                {viewModal.email?.html ? (
                  <div
                    className="p-4 bg-surface-50 rounded-xl prose prose-sm max-w-none overflow-auto"
                    style={{ maxHeight: '300px' }}
                    dangerouslySetInnerHTML={{ __html: viewModal.email.html }}
                  />
                ) : viewModal.email?.text ? (
                  <div className="p-4 bg-surface-50 rounded-xl whitespace-pre-wrap text-surface-700" style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {viewModal.email.text}
                  </div>
                ) : (
                  <div className="p-4 bg-surface-50 rounded-xl text-surface-400 italic">
                    No email content available
                  </div>
                )}
              </div>

              {/* Attachments */}
              {viewModal.email?.attachments?.length > 0 && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Attachments ({viewModal.email.attachments.length})
                  </label>
                  <div className="mt-2 space-y-2">
                    {viewModal.email.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-surface-50 rounded-xl">
                        <div>
                          <p className="text-sm font-medium text-surface-900">{att.filename}</p>
                          <p className="text-xs text-surface-500">{att.contentType}</p>
                        </div>
                        {att.size && (
                          <span className="text-xs text-surface-500">
                            {(att.size / 1024).toFixed(1)} KB
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reply History */}
              {viewModal.email?.replies?.length > 0 && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide flex items-center gap-2 mb-3">
                    <Send className="w-4 h-4" /> Replies Sent ({viewModal.email.replies.length})
                  </label>
                  <div className="space-y-3">
                    {viewModal.email.replies.map((reply, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-blue-900">{reply.subject}</span>
                          <span className="text-xs text-blue-600">
                            {new Date(reply.sentAt).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <p className="text-sm text-blue-800 whitespace-pre-wrap">{reply.message}</p>
                        <p className="text-xs text-blue-500 mt-2">Sent by: {reply.sentBy}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked User */}
              {viewModal.email?.user && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">Linked User</label>
                  <div className="mt-2 p-3 bg-surface-50 rounded-xl">
                    <p className="font-medium text-surface-900">{viewModal.email.user.name}</p>
                    <p className="text-sm text-surface-500">{viewModal.email.user.email}</p>
                  </div>
                </div>
              )}

              {/* Processing Error */}
              {viewModal.email?.processingError && (
                <div className="pt-4 border-t border-surface-200">
                  <label className="text-xs font-medium text-expense-500 uppercase tracking-wide flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Processing Error
                  </label>
                  <p className="mt-2 p-3 bg-expense-50 rounded-xl text-sm text-expense-700">
                    {viewModal.email.processingError}
                  </p>
                </div>
              )}

              {/* Status & ID */}
              <div className="pt-4 border-t border-surface-200 flex items-center justify-between">
                <div>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(viewModal.email?.status)}`}>
                    {viewModal.email?.status?.charAt(0).toUpperCase() + viewModal.email?.status?.slice(1)}
                  </span>
                </div>
                <p className="text-xs text-surface-400 font-mono">{viewModal.email?.resendEmailId}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-expense-100 rounded-full">
                  <Trash2 className="w-6 h-6 text-expense-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-surface-900">Delete Email</h2>
                  <p className="text-sm text-surface-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-surface-700 mb-6">
                Are you sure you want to delete this email from <strong>{deleteModal.email?.from}</strong>?
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteModal({ show: false, email: null })}
                  className="px-4 py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-expense-600 text-white rounded-xl font-medium hover:bg-expense-700 disabled:opacity-50"
                >
                  {deleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {replyModal.show && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="p-4 sm:p-6 border-b border-surface-200 flex-shrink-0">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-primary-100 rounded-lg flex-shrink-0">
                    <Reply className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-surface-900">Reply to Email</h2>
                    <p className="text-sm text-surface-500 truncate">
                      Replying to: {replyModal.email?.from}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setReplyModal({ show: false, email: null });
                    setReplyData({ subject: '', message: '' });
                  }}
                  className="p-2 hover:bg-surface-100 rounded-lg flex-shrink-0"
                >
                  <X className="w-5 h-5 text-surface-500" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* From/To Info */}
              <div className="p-3 sm:p-4 bg-surface-50 rounded-xl space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                  <span className="font-medium text-surface-500 sm:w-16">From:</span>
                  <span className="text-surface-900 break-all">{replyModal.email?.to?.[0] || 'N/A'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm">
                  <span className="font-medium text-surface-500 sm:w-16">To:</span>
                  <span className="text-surface-900 break-all">{replyModal.email?.from}</span>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={replyData.subject}
                  onChange={(e) => setReplyData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Email subject..."
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Message</label>
                <textarea
                  value={replyData.message}
                  onChange={(e) => setReplyData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Type your reply here..."
                />
              </div>

              {/* Original Email Preview */}
              <div className="pt-4 border-t border-surface-200">
                <label className="text-xs font-medium text-surface-500 uppercase tracking-wide">Original Message</label>
                <div className="mt-2 p-3 bg-surface-50 rounded-xl border-l-4 border-surface-300">
                  <p className="text-xs text-surface-500 mb-2 break-all">
                    On {replyModal.email?.receivedAt ? new Date(replyModal.email.receivedAt).toLocaleString() : 'N/A'}, {replyModal.email?.from} wrote:
                  </p>
                  <div className="text-sm text-surface-600 max-h-24 sm:max-h-32 overflow-y-auto">
                    {replyModal.email?.text?.substring(0, 500) || '(No content)'}
                    {replyModal.email?.text?.length > 500 && '...'}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-surface-200 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setReplyModal({ show: false, email: null });
                  setReplyData({ subject: '', message: '' });
                }}
                className="px-3 sm:px-4 py-2 sm:py-2.5 text-surface-700 hover:bg-surface-100 rounded-xl font-medium text-sm sm:text-base"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={sending || !replyData.subject || !replyData.message}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 text-sm sm:text-base"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Notifications Tab - Broadcast notifications to users
function NotificationsTab() {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    isBroadcast: true
  });

  // Expanded notification for preview
  const [expandedNotifId, setExpandedNotifId] = useState(null);
  // Delete confirmation modal
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  // Target selection state
  const [targetMode, setTargetMode] = useState('all'); // 'all', 'specific', or 'status'
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]); // array of { _id, name, email }
  const [statusTab, setStatusTab] = useState('trial');
  const [statusUsers, setStatusUsers] = useState([]);
  const [statusUsersLoading, setStatusUsersLoading] = useState(false);

  // Notification type options
  const notificationTypes = [
    { value: 'info', label: 'Info', icon: Info, color: 'bg-blue-100 text-blue-600' },
    { value: 'success', label: 'Success', icon: CheckCircle, color: 'bg-green-100 text-green-600' },
    { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'bg-amber-100 text-amber-600' },
    { value: 'error', label: 'Error', icon: AlertCircle, color: 'bg-red-100 text-red-600' },
    { value: 'update', label: 'Update', icon: Megaphone, color: 'bg-purple-100 text-purple-600' },
    { value: 'tip', label: 'Tip', icon: Lightbulb, color: 'bg-yellow-100 text-yellow-600' }
  ];

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Fetch users when switching to specific mode
  useEffect(() => {
    if (targetMode === 'specific' && allUsers.length === 0) {
      fetchUsers();
    }
    if (targetMode === 'status') {
      fetchStatusUsers(statusTab);
    }
  }, [targetMode, statusTab]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const { data } = await adminAPI.getUsers({ limit: 500 });
      setAllUsers(data.data.users || []);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchStatusUsers = async (status) => {
    try {
      setStatusUsersLoading(true);
      const { data } = await adminSubscriptionsAPI.getSubscriptions({ status, limit: 500 });
      const subs = data.data.subscriptions || [];
      setStatusUsers(subs.filter(s => s.user).map(s => ({
        _id: s.user._id || s.user,
        name: s.user.name || '',
        email: s.user.email || '',
        status: s.status,
        isPaid: s.isPaid,
        isCancelling: s.isCancelling
      })));
    } catch (err) {
      toast.error(err.error || 'Failed to fetch users');
    } finally {
      setStatusUsersLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await adminNotificationAPI.getAll({ limit: 50 });
      setNotifications(data.data.notifications || []);
    } catch (err) {
      toast.error(err.error || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u._id === user._id);
      if (exists) return prev.filter(u => u._id !== user._id);
      return [...prev, { _id: user._id, name: user.name, email: user.email }];
    });
  };

  const filteredUsers = allUsers.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if ((targetMode === 'specific' || targetMode === 'status') && selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: formData.title.trim(),
        message: formData.message.trim(),
        type: formData.type
      };

      if (targetMode === 'specific' || targetMode === 'status') {
        payload.targetUsers = selectedUsers.map(u => u._id);
      }

      await adminNotificationAPI.broadcast(payload);
      toast.success(
        targetMode === 'all'
          ? 'Notification sent to all users'
          : `Notification sent to ${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''}`
      );
      setFormData({ title: '', message: '', type: 'info', isBroadcast: true });
      setSelectedUsers([]);
      setTargetMode('all');
      setShowForm(false);
      fetchNotifications();
    } catch (err) {
      toast.error(err.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await adminNotificationAPI.delete(deleteConfirm.id);
      toast.success('Notification deleted');
      setNotifications(prev => prev.filter(n => n._id !== deleteConfirm.id));
    } catch (err) {
      toast.error(err.error || 'Failed to delete notification');
    }
    setDeleteConfirm({ open: false, id: null });
  };

  const handleResend = (notification) => {
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type || 'info',
      isBroadcast: !!notification.isBroadcast
    });
    if (notification.isBroadcast) {
      setTargetMode('all');
      setSelectedUsers([]);
    } else {
      setTargetMode('specific');
      // Pre-select the original user if available
      if (notification.user) {
        const u = notification.user;
        setSelectedUsers([{ _id: u._id || u, name: u.name || '', email: u.email || '' }]);
      } else {
        setSelectedUsers([]);
      }
      // Ensure users list is loaded
      if (allUsers.length === 0) fetchUsers();
    }
    setShowForm(true);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getTypeConfig = (type) => {
    return notificationTypes.find(t => t.value === type) || notificationTypes[0];
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-surface-900">Notifications</h2>
            <p className="text-sm text-surface-500 mt-1">Send notifications to all or specific users</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Cancel' : 'New Notification'}
          </button>
        </div>

        {/* Create Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-surface-200 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter notification title..."
                maxLength={200}
                required
              />
            </div>

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Type</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {notificationTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                        formData.type === type.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-surface-200 hover:border-surface-300'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${type.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-medium text-surface-700">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Target Selection */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Send To</label>
              <div className="flex gap-2 mb-3">
                {[
                  { mode: 'all', icon: Megaphone, label: 'All Users' },
                  { mode: 'specific', icon: Users, label: 'Specific Users' },
                  { mode: 'status', icon: Crown, label: 'By Subscription' }
                ].map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setTargetMode(mode)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 transition-all ${
                      targetMode === mode
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-surface-200 hover:border-surface-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-xs sm:text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>

              {/* Selected users chips (shared between specific and status modes) */}
              {targetMode !== 'all' && selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 mb-3 border border-surface-200 rounded-xl bg-surface-50">
                  {selectedUsers.map(u => (
                    <span
                      key={u._id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs font-medium"
                    >
                      {u.name || u.email}
                      <button type="button" onClick={() => toggleUser(u)} className="hover:text-primary-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <span className="text-[10px] text-surface-400 self-center ml-1">{selectedUsers.length} selected</span>
                </div>
              )}

              {/* User picker for specific mode */}
              {targetMode === 'specific' && (
                <div className="border border-surface-200 rounded-xl overflow-hidden">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border-none focus:outline-none focus:ring-0"
                      placeholder="Search users by name or email..."
                    />
                  </div>
                  {/* User list */}
                  <div className="max-h-48 overflow-y-auto border-t border-surface-200">
                    {usersLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <RefreshCw className="w-4 h-4 animate-spin text-surface-400" />
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="py-4 text-center text-sm text-surface-400">No users found</div>
                    ) : (
                      filteredUsers.map(user => {
                        const isSelected = selectedUsers.some(u => u._id === user._id);
                        return (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => toggleUser(user)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                              isSelected ? 'bg-primary-50' : 'hover:bg-surface-50'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'border-primary-500 bg-primary-500' : 'border-surface-300'
                            }`}>
                              {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-surface-900 truncate">{user.name}</div>
                              <div className="text-xs text-surface-500 truncate">{user.email}</div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Status-based user picker */}
              {targetMode === 'status' && (
                <div className="border border-surface-200 rounded-xl overflow-hidden">
                  {/* Status tabs */}
                  <div className="flex border-b border-surface-200 bg-surface-50 overflow-x-auto">
                    {[
                      { key: 'trial', label: 'Trial', color: 'text-amber-700' },
                      { key: 'active', label: 'Active', color: 'text-green-700' },
                      { key: 'cancelling', label: 'Cancelling', color: 'text-orange-700' },
                      { key: 'paused', label: 'Paused', color: 'text-blue-700' },
                      { key: 'cancelled', label: 'Cancelled', color: 'text-surface-600' },
                      { key: 'expired', label: 'Expired', color: 'text-red-700' }
                    ].map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setStatusTab(tab.key)}
                        className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                          statusTab === tab.key
                            ? `${tab.color} border-current bg-white`
                            : 'text-surface-400 border-transparent hover:text-surface-600'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {/* Select all / none */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-surface-100 bg-white">
                    <span className="text-xs text-surface-500">{statusUsers.length} users</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newUsers = statusUsers.filter(u => !selectedUsers.some(s => s._id === u._id));
                          setSelectedUsers(prev => [...prev, ...newUsers]);
                        }}
                        className="text-[11px] font-medium text-primary-600 hover:text-primary-700"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const statusIds = new Set(statusUsers.map(u => u._id));
                          setSelectedUsers(prev => prev.filter(u => !statusIds.has(u._id)));
                        }}
                        className="text-[11px] font-medium text-surface-400 hover:text-surface-600"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  {/* User list */}
                  <div className="max-h-48 overflow-y-auto">
                    {statusUsersLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <RefreshCw className="w-4 h-4 animate-spin text-surface-400" />
                      </div>
                    ) : statusUsers.length === 0 ? (
                      <div className="py-4 text-center text-sm text-surface-400">No users with this status</div>
                    ) : (
                      statusUsers.map(user => {
                        const isSelected = selectedUsers.some(u => u._id === user._id);
                        return (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => toggleUser(user)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                              isSelected ? 'bg-primary-50' : 'hover:bg-surface-50'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'border-primary-500 bg-primary-500' : 'border-surface-300'
                            }`}>
                              {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-surface-900 truncate">{user.name}</div>
                              <div className="text-xs text-surface-500 truncate">{user.email}</div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Message - Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">
                Message
                <span className="text-surface-400 font-normal ml-2">(HTML supported)</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[200px] resize-y font-mono text-sm"
                placeholder="Enter notification message... (HTML tags like <b>, <i>, <ul>, <li>, <a href='...'> are supported)"
                maxLength={10000}
                required
              />
              <div className="mt-2 flex items-center justify-between text-xs text-surface-500">
                <span>Supported: &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;a href="..."&gt;, &lt;br&gt;</span>
                <span>{formData.message.length}/10000</span>
              </div>
            </div>

            {/* Preview */}
            {formData.message && (
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">Preview</label>
                <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    {(() => {
                      const config = getTypeConfig(formData.type);
                      const Icon = config.icon;
                      return (
                        <div className={`p-2 rounded-lg flex-shrink-0 ${config.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-surface-900">{formData.title || 'Notification Title'}</h4>
                      <div
                        className="mt-1 text-sm text-surface-600 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: formData.message }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={sending || !formData.title || !formData.message || (targetMode === 'specific' && selectedUsers.length === 0)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {targetMode === 'all' ? 'Send to All Users' : `Send to ${selectedUsers.length || '...'} User${selectedUsers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-2xl border border-surface-200">
        <div className="p-4 border-b border-surface-200">
          <h3 className="font-semibold text-surface-900">Recent Notifications ({notifications.length})</h3>
        </div>

        {notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-surface-300 mb-3" />
            <p className="text-surface-500">No notifications sent yet</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-100">
            {notifications.map((notification) => {
              const config = getTypeConfig(notification.type);
              const Icon = config.icon;
              const isExpanded = expandedNotifId === notification._id;
              return (
                <div key={notification._id} className="hover:bg-surface-50 transition-colors">
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedNotifId(isExpanded ? null : notification._id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-surface-900 truncate">{notification.title}</h4>
                            {!isExpanded && (
                              <div
                                className="mt-1 text-sm text-surface-600 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: notification.message }}
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleResend(notification); }}
                              className="p-1.5 text-surface-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Resend / Edit & Send"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, id: notification._id }); }}
                              className="p-1.5 text-surface-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete notification"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-surface-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatAdminDate(notification.createdAt)}
                          </span>
                          {notification.isBroadcast ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                              <Megaphone className="w-3 h-3" />
                              All Users
                            </span>
                          ) : notification.user && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              <Users className="w-3 h-3" />
                              {notification.user.name || notification.user.email || 'Specific User'}
                            </span>
                          )}
                          {notification.isBroadcast ? (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {notification.readBy?.length || 0} read
                            </span>
                          ) : (
                            <span className={`flex items-center gap-1 ${notification.isRead ? 'text-green-600' : ''}`}>
                              <Eye className="w-3 h-3" />
                              {notification.isRead ? 'Read' : 'Unread'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded full message view */}
                  {isExpanded && (
                    <div className="px-4 pb-4 ml-11">
                      <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
                        <div
                          className="prose prose-sm max-w-none text-surface-700 break-words [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all [&_a]:hover:text-blue-800"
                          dangerouslySetInnerHTML={{
                            __html: notification.message.replace(
                              /(https?:\/\/[^\s<>"']+)/g,
                              '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
                            )
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This will remove it for all users."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

// Notification Channels Tab
function NotificationChannelsTab() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    smsEnabled: false,
    whatsappEnabled: false,
    actionChannels: {
      budgetAlerts: 'none',
      securityAlerts: 'none',
      taxReminders: 'none',
      debtReminders: 'none',
      receivableReminders: 'none',
      weeklySummary: 'none',
      monthlySummary: 'none'
    }
  });

  const actionTypes = [
    { key: 'budgetAlerts', label: 'Budget Alerts', description: 'Alerts when spending exceeds budget limits' },
    { key: 'securityAlerts', label: 'Security Alerts', description: 'Login notifications and password changes' },
    { key: 'taxReminders', label: 'Tax Reminders', description: 'Advance tax payment due date reminders' },
    { key: 'debtReminders', label: 'Debt Reminders', description: 'Payment due date reminders for debts' },
    { key: 'receivableReminders', label: 'Receivable Reminders', description: 'Overdue payment collection reminders' },
    { key: 'weeklySummary', label: 'Weekly Summary', description: 'Weekly financial summary reports' },
    { key: 'monthlySummary', label: 'Monthly Summary', description: 'Monthly financial summary reports' }
  ];

  const channelOptions = [
    { value: 'none', label: 'Email Only' },
    { value: 'whatsapp', label: 'Email + WhatsApp' },
    { value: 'sms', label: 'Email + SMS' },
    { value: 'both', label: 'Email + WhatsApp + SMS' }
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await adminNotificationChannelsAPI.getConfig();
      setConfig(data.data.channels);
    } catch (error) {
      showToast('Failed to load notification channel configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminNotificationChannelsAPI.updateConfig(config);
      showToast('Notification channels updated successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to update notification channels', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setConfig(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleActionChannelChange = (action, value) => {
    setConfig(prev => ({
      ...prev,
      actionChannels: {
        ...prev.actionChannels,
        [action]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-surface-400" />
          <span className="ml-2 text-surface-500">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Smartphone className="w-6 h-6 text-accent" />
          <h2 className="text-lg font-semibold text-surface-900">Notification Channels</h2>
        </div>
        <p className="text-sm text-surface-500">
          Configure which channels (SMS, WhatsApp) are used for different notification types. Email is always enabled by default.
        </p>
      </div>

      {/* Global Channel Availability */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="text-base font-semibold text-surface-900 mb-4">Channel Availability</h3>
        <p className="text-sm text-surface-500 mb-4">
          Enable or disable SMS and WhatsApp channels globally. These require Twilio configuration.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* SMS Toggle */}
          <div className="flex items-center justify-between p-4 border border-surface-200 rounded-xl bg-surface-50">
            <div>
              <p className="font-medium text-surface-900">SMS Notifications</p>
              <p className="text-sm text-surface-500">Requires Twilio configuration</p>
            </div>
            <button
              onClick={() => handleToggle('smsEnabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.smsEnabled ? 'bg-income-500' : 'bg-surface-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.smsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {/* WhatsApp Toggle */}
          <div className="flex items-center justify-between p-4 border border-surface-200 rounded-xl bg-surface-50">
            <div>
              <p className="font-medium text-surface-900">WhatsApp Notifications</p>
              <p className="text-sm text-surface-500">Requires Twilio WhatsApp setup</p>
            </div>
            <button
              onClick={() => handleToggle('whatsappEnabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.whatsappEnabled ? 'bg-income-500' : 'bg-surface-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.whatsappEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Per-Action Channel Configuration */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="text-base font-semibold text-surface-900 mb-2">Notification Channels by Action</h3>
        <p className="text-sm text-surface-500 mb-4">
          Configure which channels to use for each notification type. Email is always enabled for all notification types.
        </p>

        <div className="space-y-3">
          {actionTypes.map(action => (
            <div key={action.key} className="flex items-center justify-between p-4 border border-surface-200 rounded-xl hover:bg-surface-50 transition-colors">
              <div className="flex-1">
                <p className="font-medium text-surface-900">{action.label}</p>
                <p className="text-sm text-surface-500">{action.description}</p>
              </div>
              <select
                value={config.actionChannels[action.key]}
                onChange={(e) => handleActionChannelChange(action.key, e.target.value)}
                className="px-4 py-2 border border-surface-200 rounded-lg bg-white text-surface-900 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent min-w-[180px]"
              >
                {channelOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Info Note */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Note:</strong> SMS and WhatsApp channels require users to verify their phone number before receiving notifications.
          The channel selection here sets the default for all users. Individual users can still manage their preferences in their notification settings.
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
}

// AI Settings Tab
function ReferralsTab() {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    enabled: false,
    minTransactions: 5,
    requiredReferrals: 10,
    discountType: 'percentage',
    discountValue: 20,
    shareMessage: '',
    referralBaseUrl: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await adminReferralAPI.getConfig();
      setConfig(data.data.config);
      setStats(data.data.stats);
      setFormData({
        enabled: data.data.config.enabled || false,
        minTransactions: data.data.config.minTransactions || 5,
        requiredReferrals: data.data.config.requiredReferrals || 10,
        discountType: data.data.config.discountType || 'percentage',
        discountValue: data.data.config.discountValue || 20,
        shareMessage: data.data.config.shareMessage || '',
        referralBaseUrl: data.data.config.referralBaseUrl || ''
      });
    } catch (err) {
      toast.error('Failed to load referral config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await adminReferralAPI.updateConfig(formData);
      toast.success('Referral config updated');
      fetchConfig();
    } catch (err) {
      toast.error(err.error || 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6 flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-surface-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl border border-surface-200 p-4 text-center">
            <div className="text-xs text-surface-400 uppercase tracking-wide">Total Referrals</div>
            <div className="text-2xl font-bold text-surface-900 mt-1">{stats.totalReferrals}</div>
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 p-4 text-center">
            <div className="text-xs text-surface-400 uppercase tracking-wide">Qualified</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.qualifiedReferrals}</div>
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 p-4 text-center">
            <div className="text-xs text-surface-400 uppercase tracking-wide">Rewards Earned</div>
            <div className="text-2xl font-bold text-surface-900 mt-1">{stats.rewardsEarned}</div>
          </div>
          <div className="bg-white rounded-2xl border border-surface-200 p-4 text-center">
            <div className="text-xs text-surface-400 uppercase tracking-wide">Conversion Rate</div>
            <div className="text-2xl font-bold text-surface-900 mt-1">{stats.conversionRate}%</div>
          </div>
        </div>
      )}

      {/* Config Form */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h2 className="text-lg font-semibold text-surface-900 mb-6">Referral Program Settings</h2>

        <div className="space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-surface-900">Enable Referral Program</label>
              <p className="text-sm text-surface-500">Allow users to refer friends and earn rewards</p>
            </div>
            <button
              onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="relative"
            >
              {formData.enabled ? (
                <ToggleRight className="w-10 h-10" style={{ color: 'var(--accent-bg)' }} />
              ) : (
                <ToggleLeft className="w-10 h-10 text-surface-300" />
              )}
            </button>
          </div>

          {/* Min Transactions */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Minimum Transactions (referee must add)
            </label>
            <input
              type="number"
              min="1"
              value={formData.minTransactions}
              onChange={(e) => setFormData(prev => ({ ...prev, minTransactions: parseInt(e.target.value) || 1 }))}
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
            />
          </div>

          {/* Required Referrals */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Required Qualified Referrals (to earn reward)
            </label>
            <input
              type="number"
              min="1"
              value={formData.requiredReferrals}
              onChange={(e) => setFormData(prev => ({ ...prev, requiredReferrals: parseInt(e.target.value) || 1 }))}
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
            />
          </div>

          {/* Discount Type */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">Discount Type</label>
            <select
              value={formData.discountType}
              onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="flat">Flat Amount</option>
            </select>
          </div>

          {/* Discount Value */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Discount Value {formData.discountType === 'percentage' ? '(%)' : '(amount)'}
            </label>
            <input
              type="number"
              min="0"
              value={formData.discountValue}
              onChange={(e) => setFormData(prev => ({ ...prev, discountValue: parseFloat(e.target.value) || 0 }))}
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
            />
          </div>

          {/* Referral Base URL */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Referral Base URL
            </label>
            <input
              type="text"
              value={formData.referralBaseUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, referralBaseUrl: e.target.value }))}
              placeholder="https://hisabjourney.com"
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600"
            />
            <p className="text-xs text-surface-400 mt-1">Marketing site URL where /register lives. Leave empty to use app origin.</p>
          </div>

          {/* Share Message */}
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1">
              Share Message Template
            </label>
            <textarea
              value={formData.shareMessage}
              onChange={(e) => setFormData(prev => ({ ...prev, shareMessage: e.target.value }))}
              placeholder="Use {link} as placeholder for the referral link"
              rows={3}
              className="w-full px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-theme-600 resize-none"
            />
            <p className="text-xs text-surface-400 mt-1">Use {'{link}'} as placeholder for the referral link</p>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
